import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { confirmDialog } from '../confirm';
import { askStartGate } from '../start-gate';
import { holdingUnsaved } from '../unsaved';
import { modKey } from '../platform';
import { useApp } from '../store';
import CardSprint from './CardSprint';
import type { BlockerType, BlockerEntry, ChecklistItem, IssueKind, IssuePriority, IssueSize, IssueStatus, MoscowLevel, InvestmentClass, StatusUpdate } from '../types';
import InvestmentField from './InvestmentField';
import { derivedInvestmentClass, investmentClassOf, investmentLifecycleDefault, INVESTMENT_LABEL, INVESTMENT_TONE } from '../investment';
import { blockersOf, stillBlocked, cardDepsOf, otherBlockersOf } from '../blocked';
import { SIZE_HINT, SIZE_LABEL, SIZE_ORDER, liveAreas, labelColour } from '../types';
import { nextAreaColour } from '../area-colours';
import { areaForText } from '../area-match';
import { misfiledAs, type Misfiled } from '../misfiled';
import { livePhases, cardDueState, daysPast, goLiveTargetMs } from '../phases';
import { dueSlip, startSlip, slipLabel, type DateSlip } from '../card-slip';
import { progressHealth, healthOfIssue } from '../progress-health';
import { effectiveProgress } from '../progress-derive';
import { ProgressMini } from './ProgressMini';
import CardChecklist from './CardChecklist';
import { MONTHS, shortDay, humanDay } from '../when';
import DateField from './DateField';
import { useInvalidDates } from './useInvalidDates';
import { canPlanInto, inOrder } from '../sprints';
import { suggestSize, suggestDue, SIZE_WORD } from '../suggest';
import { ownerOf, releaseOutlook } from '../forecast';
import { echoesTheType, labelOf, liveTypes, meansMark, meansOf } from '../issue-types';
import { ACTUAL_PARKED_LABEL, MOSCOW_LABELS, MOSCOW_ORDER, PRIORITY_LABELS, PRIORITY_ORDER, STATUS_LABELS } from '../types';
import { statusOrderFor, boardLifecycleTier, isProjectMode } from '../lifecycle';
import { isInService } from '../handoff';
import { ListEditable } from './ListEditable';
import type { MentionEditableHandle } from './MentionEditable';
import { historyTime, relativeTime } from '../utils';
import Priority, { PriorityGlyph } from './Priority';
import Icon from './Icon';
import NavIcon from './NavIcon';
import IssueText from './IssueText';
import BlockerSection, { type BlockerSectionHandle } from './BlockerSection';
import DependencySection, { type DependencySectionHandle } from './DependencySection';
import CardFeed from './CardFeed';
import InfoTip from './InfoTip';
import AreaField from './AreaField';
import EpicField from './EpicField';
import LabelField from './LabelField';
import PickField from './PickField';
import VendorTag from './VendorTag';
import { useSampleInert } from './SampleStrip';
import Reveal from './Reveal';
import Expander from './Expander';
import Collapsible from './Collapsible';
import { can, canSeeCompletionOdds } from '../permissions';
import { outstandingChecks, doneAllowed } from '../done';
import { checklistComplete, checklistPromptsOf } from '../checklist-prompts';
import { displayName, initials } from '../people';
import { useDismiss, useFocusTrap } from '../motion';
import { assigneeAssistant, assistantAssignee, assistantsOf, defaultAssignee } from '../assistant';
import { cardCreateDefault } from '../company-role';
import { boardAssignees } from '../projects';
import { DEFAULT_SOURCE_NAME, ownedByBoard, type IssueSource } from '../ownership';
import { decisionFromBlocker } from '../decisions';

// ── ACTUAL DATES — planned vs actual on the card ─────────────────────────────
// The card already stamps when work really began (`startedAt`, first move into In
// progress) and when it really finished (`completedAt`, entering Done/Won't-do).
// These helpers show that alongside the planned start/target so the slip is
// visible — planned 15 Sep, actually finished 18 Sep — Vinnie, 19 Sep.
const D_DAY_MS = 86_400_000;

/** An absolute ms instant as a short local day — "15 Sept". */
function msShortDay(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]}`;
}
/** The ms instant as a year-bearing day — "6 Sept 2026" — the read-mode companion to
 *  `msShortDay`, so a full date shown to READ (not the compact card chip) carries its year
 *  per R16, agreeing with `humanDay`/`asDate`. */
function msHumanDay(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
/** The ms instant as a plain `YYYY-MM-DD` local day, so it diffs against a
 *  planned day on the same calendar-day footing. */
function msToIsoDay(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
/** "2 days late" / "3 days early" / "on the day", comparing an actual day to a
 *  planned day; null when there is no planned day to compare against. */
function slipWord(actualIso: string, plannedIso?: string | null): string | null {
  if (!plannedIso) return null;
  const u = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const n = Math.round((u(actualIso) - u(plannedIso)) / D_DAY_MS);
  if (n === 0) return 'on the day';
  const days = Math.abs(n);
  return `${days} day${days === 1 ? '' : 's'} ${n > 0 ? 'late' : 'early'}`;
}

/** The variance tail beside a date that has moved from its baseline — "· originally
 *  12 Sep ( +14d · moved 3× )". The slip reads amber, and red only when the card
 *  is also overdue (slip is a warning; lateness is the alarm). Renders nothing
 *  when the date has not moved. Migration 0125; see src/card-slip.ts. */
function SlipNote({ slip, over }: { slip: DateSlip; over?: boolean }) {
  if (!slip.moved || slip.baseline == null || slip.slipDays == null) return null;
  return (
    <>
      <span className="slip-orig"> · originally {shortDay(slip.baseline)}</span>
      <span className="slip-var-wrap">
        {' ('}
        <span className={over ? 'slip-var over' : 'slip-var'}>{slipLabel(slip.slipDays)}</span>
        {slip.pushes > 0 && <span className="slip-count"> · moved {slip.pushes}×</span>}
        {')'}
      </span>
    </>
  );
}

/**
 * THE CARD FACE — who is doing the work and who is accountable, as one
 * overlapping avatar pile with a plain-words line beside it. The doer
 * (Responsible) sits in front and tinted; the Accountable person behind. Same
 * person → one avatar. The labelled Accountable/Responsible fields stay the
 * source of truth; this is the at-a-glance summary of the two (two-part
 * assignee, Feature A).
 *
 * Two variants, one markup, so the two states can never drift apart:
 *   'line'  — folded onto the opened card's status line (read view): pile +
 *             words, no edit affordance.
 *   'block' — the editing view: the same pile + words, and when no doer is named
 *             yet a ghost "+" that opens the Responsible picker in place.
 */
function CardFace({
  assignee,
  responsible,
  meta,
  profiles,
  assignable,
  editable,
  variant,
  pickDoer,
  setPickDoer,
  setResponsible,
}: {
  assignee: string;
  responsible: string;
  meta: Parameters<typeof assigneeAssistant>[1];
  profiles: Parameters<typeof displayName>[1];
  assignable: string[];
  editable: boolean;
  variant: 'line' | 'block';
  pickDoer: boolean;
  setPickDoer: (v: boolean) => void;
  setResponsible: (v: string) => void;
}) {
  const acct = (assignee ?? '').trim();
  const doer = (responsible ?? '').trim();
  if (!acct && !doer && !editable) return null;
  const same = !!acct && !!doer && acct === doer;
  const acctBot = acct ? assigneeAssistant(acct, meta) : null;
  const doerBot = doer ? assigneeAssistant(doer, meta) : null;
  const nameOf = (v: string, bot: ReturnType<typeof assigneeAssistant>) =>
    bot ? bot.name?.trim() || bot.id : displayName(v, profiles);
  const av = (v: string, bot: ReturnType<typeof assigneeAssistant>, isDoer: boolean, title: string) => (
    <span className={`cardface-av${isDoer ? ' is-doer' : ''}`} title={title}>
      {bot ? <Icon name="sparkle" size={12} /> : initials(v, profiles)}
    </span>
  );
  const doerOptions = [
    { value: '', label: 'Unassigned' },
    ...assignable.map((m) => ({
      value: m,
      label: displayName(m, profiles),
      trailing: <VendorTag who={m} profiles={profiles} compact />,
    })),
    ...assistantsOf(meta).map((a) => ({
      value: assistantAssignee(a.id),
      label: a.name?.trim() || a.id,
      group: 'Assistants',
    })),
    ...(responsible && !assignable.includes(responsible) && !assigneeAssistant(responsible, meta)
      ? [{ value: responsible, label: responsible, trailing: <VendorTag who={responsible} profiles={profiles} compact /> }]
      : []),
  ];
  return (
    <span className={variant === 'line' ? 'cardface cardface-line' : 'cardface'}>
      <span className="cardface-avs">
        {same ? (
          av(acct, acctBot, true, `${nameOf(acct, acctBot)} is doing this and is accountable`)
        ) : (
          <>
            {doer && av(doer, doerBot, true, `${nameOf(doer, doerBot)} is doing this`)}
            {acct && av(acct, acctBot, false, `${nameOf(acct, acctBot)} is accountable`)}
            {!doer &&
              editable &&
              (pickDoer ? (
                <span className="cardface-pick">
                  <PickField
                    ariaLabel="Responsible"
                    value={responsible}
                    options={doerOptions}
                    autoOpen
                    onChange={(v) => {
                      setResponsible(v);
                      setPickDoer(false);
                    }}
                    onClose={() => setPickDoer(false)}
                  />
                </span>
              ) : (
                <button
                  type="button"
                  className="cardface-add"
                  title="Name who is doing this"
                  onClick={() => setPickDoer(true)}
                >
                  <Icon name="plus" size={13} />
                </button>
              ))}
          </>
        )}
      </span>
      <span className="cardface-words">
        {same ? (
          <>
            <b>{nameOf(acct, acctBot)}</b>
            <VendorTag who={acct} profiles={profiles} /> is doing this &amp; is accountable
          </>
        ) : doer ? (
          <>
            <b>{nameOf(doer, doerBot)}</b>
            <VendorTag who={doer} profiles={profiles} /> is doing this
            {acct && (
              <>
                {' '}· {nameOf(acct, acctBot)}
                <VendorTag who={acct} profiles={profiles} /> accountable
              </>
            )}
          </>
        ) : acct ? (
          <>
            {nameOf(acct, acctBot)}
            <VendorTag who={acct} profiles={profiles} /> accountable
            {editable ? <> · name who is doing this</> : <> · nobody doing this yet</>}
          </>
        ) : (
          <>Name who is doing this</>
        )}
      </span>
    </span>
  );
}

export default function IssueModal() {
  const selectedId = useApp((s) => s.selectedId);
  const creating = useApp((s) => s.creating);
  const issue = useApp((s) => s.issues.find((i) => i.id === s.selectedId));
  const access = useApp((s) => s.access);
  const profiles = useApp((s) => s.profiles);
  const role = useApp((s) => s.shownRole);
  const canEditIssue = can(role, 'editIssue');
  const inert = useSampleInert();
  /* The same capability the sprint screen gates its Add and Take out on —
     putting a card in a fortnight is moving it, not editing it. */
  const mayPlan = can(role, 'moveIssue');
  const canDelete = can(role, 'deleteIssue');
  const canComment = can(role, 'comment');
  const closeModal = useApp((s) => s.closeModal);
  const setView = useApp((s) => s.setView);
  // Where the Portfolio was when this card was opened from it — null for a card
  // opened any other way. When it is set, every close route returns to the
  // Portfolio instead of just shutting: the card was a detour, so leaving it
  // lands back where the viewer started, never on a board they never chose.
  const portfolioReturn = useApp((s) => s.portfolioReturn);
  const leaveModal = useCallback(
    () => (portfolioReturn ? setView('portfolio') : closeModal()),
    [portfolioReturn, setView, closeModal],
  );
  // Closing runs the leaving animation first — see src/motion.ts. Everything
  // that shuts this card goes through `dismiss`, or one route would vanish while
  // the others faded and it would read as a glitch on that one control.
  const { leaving, dismiss } = useDismiss(leaveModal);
  const createIssue = useApp((s) => s.createIssue);
  const updateIssue = useApp((s) => s.updateIssue);
  const logDecision = useApp((s) => s.logDecision);
  const setIssueEditing = useApp((s) => s.setIssueEditing);
  // The card-face "+ What's this about?" nudge opens a summary-less card straight
  // into writing its summary (store.openEditField); consumed once, below.
  const openEditField = useApp((s) => s.openEditField);
  const clearOpenEditField = useApp((s) => s.clearOpenEditField);
  // The summary box is a contenteditable (ListEditable) so a list it holds hangs
  // its wrapped lines the way the composer does; this handle focuses and scrolls to
  // it for the "+ What's this about?" nudge. (The list-format caret is restored
  // inside the editor now, so the old stashed-caret ref and effect are gone.)
  const summaryEditor = useRef<MentionEditableHandle | null>(null);
  const deleteIssue = useApp((s) => s.deleteIssue);
  const holdIssue = useApp((s) => s.holdIssue);
  const resumeIssue = useApp((s) => s.resumeIssue);
  const assistDismiss = useApp((s) => s.assistDismiss);
  const dismissAssist = useApp((s) => s.dismissAssist);
  /** Why nothing was deleted. Silence would read as the button being broken. */
  const [deleteError, setDeleteError] = useState('');
  const allIssues = useApp((s) => s.issues);
  const repoUrl = useApp((s) => s.meta.repoUrl ?? '');
  const source = useApp((s) => (s.meta.source ?? 'tracker') as IssueSource);
  const sourceName = useApp((s) => s.meta.sourceName?.trim() || DEFAULT_SOURCE_NAME);
  // Reading the wording is for boards fed from somewhere else, where nobody is
  // present to answer. Where people manage the board themselves, a guess at the
  // sentence they just typed is not an answer — it is that sentence again.
  const guesses = source === 'tracker';
  // A board fed from a tracker holds a TRANSLATION: something technical was
  // written elsewhere and someone rendered it readable. A board a team keeps
  // itself holds the original — nobody is translating anything, and asking for
  // "plain English for non-technical readers" implies a technical version
  // exists that they have not written and do not owe anyone.
  const WORDS = guesses
    ? {
        summaryLabel: 'Plain English',
        summaryHint: '(what this is about, for non-technical readers)',
        notes: 'Technical Notes',
        summaryMissing:
          'No plain-English explanation yet — add one so anyone reading this knows what it is about.',
      }
    : {
        summaryLabel: 'What is this about?',
        summaryHint: '(a sentence or two anyone on the team could follow)',
        notes: 'More Detail',
        summaryMissing: 'No description yet — add one so this is not just a title.',
      };
  // A field the tracker owns is not merely unsaved if edited here — it is saved
  // and then silently undone by the next push, which is worse than refusing it.
  const mine = (f: Parameters<typeof ownedByBoard>[0]) => ownedByBoard(f, source);
  const sprints = useApp((s) => s.sprints);
  const putInSprint = useApp((s) => s.putInSprint);
  // Absent is flow — see ProjectMeta.cadence. The field is not offered at all
  // on a board that does not work in timeboxes.
  const inSprints = useApp((s) => s.meta.cadence) === 'sprints';
  /* Whether the Sprint row has anything to say: the card is in a sprint (running
     or finished — CardSprint names it either way), or there is an open sprint to
     plan it into. Without this the row drew a bare "Sprint" label with nothing
     beside it whenever CardSprint had nothing to offer. */
  const sprintRowShows =
    inSprints && (!!issue?.sprint || (mayPlan && inOrder(sprints).some(canPlanInto)));
  // Derive AFTER selecting. A selector that builds a fresh array every call never
  // compares equal, and useSyncExternalStore reads that as an infinite loop —
  // which crashed the whole tree the moment this modal mounted.
  const epicOptions = useMemo(
    () => [...new Set(allIssues.map((i) => i.epic).filter(Boolean))].sort() as string[],
    [allIssues]
  );

  const isNew = creating && !selectedId;
  // Keep keyboard focus inside the open card, and hand it back on close — see
  // src/motion.ts. Armed whenever the card is on screen; the ref sits on the
  // panel, which carries tabIndex={-1} so it can hold focus itself.
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isNew || !!issue);
  // Nine times out of ten an issue is opened to be read, so it opens as readable
  // text. Editing is a deliberate step, and reviewers never get it.
  const [editing, setEditing] = useState(isNew);

  // Let the rest of the app (the command palette) know this card is being edited,
  // so it can withhold the quick actions the open form would clobber on Save.
  // Cleared when the modal closes, so nothing is left thinking a card is still open.
  useEffect(() => {
    setIssueEditing(editing);
    return () => setIssueEditing(false);
  }, [editing, setIssueEditing]);

  // Opened straight into writing the summary (the card-face nudge): start in edit
  // mode, and remember to focus the field. One click on "+ What's this about?"
  // turns into typing. Consumed once, then cleared so a later ordinary open of the
  // same card lands in read mode as usual.
  const [focusSummaryWanted, setFocusSummaryWanted] = useState(false);
  // Needs Attention's "Fix →" opens the card onto the field it named — the engine's
  // AttnFix maps to one of these. Consumed by the focus effect below; the field-open
  // effect that sets it sits lower, after the section toggles it has to open.
  const [focusFieldWanted, setFocusFieldWanted] = useState<'dates' | 'assignee' | 'sprint' | 'size' | 'status' | null>(null);
  useEffect(() => {
    if (openEditField === 'summary' && !isNew && canEditIssue) {
      setEditing(true);
      setFocusSummaryWanted(true);
      clearOpenEditField();
    }
    // The other targets (dates/assignee/sprint/size/status) are handled lower, where
    // the section toggles they open are in scope; this one owns only 'summary'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditField]);
  // The summary textarea only exists in the edit view, and it sits LOW in the
  // form — below the rule and the whole blocker list. So landing on it is not a
  // plain focus: the reader must be scrolled down to it, or they open the card
  // and still have to hunt for the box (Vinnie, 10 Oct — "I should be taken
  // directly to that box so I don't have to scroll").
  //
  // The scroll cannot run in the commit that turns editing on: the controls
  // above the summary (the blocker section especially) are mounting in that
  // same pass and settle their heights a frame later, which shoves the summary
  // down AFTER a synchronous scroll — so we'd scroll to where the box was, not
  // where it lands. Defer past layout (a double rAF), then land on it; a single
  // late re-land (~120ms) catches any height that grows a beat after that, e.g.
  // an auto-sized field finishing its measure. preventScroll on focus so the
  // browser's own caret-scroll doesn't fight our centring.
  useEffect(() => {
    if (!editing || !focusSummaryWanted) return;
    const land = () => {
      const ed = summaryEditor.current;
      const el = ed?.node();
      if (!ed || !el) return;
      ed.focus(); // focuses and puts the caret at the end of the summary
      el.scrollIntoView({ block: 'center' });
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(land));
    const late = setTimeout(() => { land(); setFocusSummaryWanted(false); }, 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(late); };
  }, [editing, focusSummaryWanted]);

  // FIELD-TARGETED OPEN — Needs Attention's "Fix →" lands the card on the exact
  // field the engine named (set-dates, assign, add-to-sprint, set-size, move-to-
  // status). Its row carries `data-focus="<target>"`; once edit mode and the
  // containing section have settled (same double-rAF + late re-land as the summary
  // path, because the sections above change height as they mount), we scroll that
  // row to centre and focus its first control, so one click is the whole move.
  useEffect(() => {
    if (!editing || !focusFieldWanted) return;
    const land = () => {
      const row = modalRef.current?.querySelector(`[data-focus="${focusFieldWanted}"]`) as HTMLElement | null;
      if (!row) return;
      row.scrollIntoView({ block: 'center' });
      const ctl = row.querySelector('button, input, [tabindex], select, textarea') as HTMLElement | null;
      ctl?.focus({ preventScroll: true });
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(land));
    const late = setTimeout(() => { land(); setFocusFieldWanted(null); }, 160);
    return () => { cancelAnimationFrame(raf); clearTimeout(late); };
  }, [editing, focusFieldWanted]);

  const [title, setTitle] = useState(issue?.title ?? '');
  const [summary, setSummary] = useState(issue?.summary ?? '');
  // The card's blockers, as a LIST (#456). An existing card reads its saved list
  // (or, for a card from before the list existed, the one entry derived from the
  // old single fields); a new card starts empty. The old scalar blocked /
  // blockerType / blockerTarget are no longer edited directly — they are mirrored
  // from this list on save so everything still reading them keeps working.
  const [blockerEntries, setBlockerEntries] = useState<BlockerEntry[]>(() =>
    issue ? blockersOf(issue) : []
  );
  // The blocker editor, so Save can flush a blocker the person built but did not
  // press "Add blocker" on — folding it into what we write instead of dropping it.
  const blockerRef = useRef<BlockerSectionHandle>(null);
  // The dependency editor, flushed the same way: a card link picked but not yet
  // "Add dependency"-ed is folded in on save rather than lost.
  const depRef = useRef<DependencySectionHandle>(null);
  /*
   * DERIVE-FIRST DECISION SEED (A) — clearing a `decision`-type blocker asks
   * "what was decided?" and logs it, so the call the card was waiting on lands in
   * the project's decision log instead of evaporating. Re-anchored onto the
   * blocker LIST (#456): the field is pre-filled from the first decision-kind
   * entry's text; `seedLogging` guards the one-click path while the write is in
   * flight. Reset per card below.
   */
  // Opens EMPTY: the blocker's reason is shown read-only above the field (below),
  // so the OUTCOME is typed fresh rather than the open question being logged as
  // the decision by a straight click-through.
  const [decidedText, setDecidedText] = useState('');
  const [seedLogging, setSeedLogging] = useState(false);
  const [seedProblem, setSeedProblem] = useState('');
  /*
   * Clearing the decision blocker drops every decision-kind entry from the list
   * and persists the remainder, re-mirroring the old single fields from the new
   * first entry — the same mirror the Save handler writes (#456), so the card-
   * face badge, Next up and the blocked.ts derive stay in step. An empty list
   * clears all four. Used by both seed-box buttons below.
   */
  const clearDecisionBlocker = () => {
    const remaining = blockerEntries.filter((e) => e.type !== 'decision');
    // Keep the working copy in step so the seed box (guarded on blockerEntries)
    // hides the moment the decision blocker is cleared, not on the next re-mount.
    setBlockerEntries(remaining);
    const p = remaining[0];
    const pCard = p?.refs.find((r) => r.kind === 'card');
    const pPerson = p?.refs.find((r) => r.kind === 'person');
    return updateIssue(issue!.id, {
      blockerRefs: remaining,
      blocked: p
        ? p.text.trim() ||
          (pCard
            ? `${pCard.ref}${pCard.label ? ` ${pCard.label}` : ''}`
            : pPerson
              ? `Waiting on ${pPerson.label || pPerson.ref}`
              : '')
        : '',
      blockerType: (p ? p.type || 'other' : '') as BlockerType | '',
      blockerTarget: pCard?.ref || pPerson?.label || pPerson?.ref || '',
    });
  };
  const [epic, setEpic] = useState(issue?.epic ?? '');
  const [description, setDescription] = useState(issue?.description ?? '');
  // A new issue started from a column's + begins in that column. Started from
  // the top bar there is no column, and it begins where new work begins.
  const createStatus = useApp((s) => s.createStatus);
  const [status, setStatus] = useState<IssueStatus>(issue?.status ?? createStatus ?? 'todo');
  /* The In Staging option appears only on a board that turned the column on — or
     when this card is already on it, so the picker always shows its real value. */
  const stagingOn = useApp((s) => s.meta.stagingEnabled === true);
  const statusChoices = statusOrderFor({ stagingEnabled: stagingOn || status === 'staging' });
  /* Whether this board has opened the completion odds to everyone (0220); absent reads as
     off — a PM-and-above read. Gates the Release outlook row below. */
  const showOddsToEveryone = useApp((s) => s.meta.showOddsToEveryone === true);
  const [priority, setPriority] = useState<IssuePriority>(issue?.priority ?? 'medium');
  /* MoSCoW — how essential for the release, absent until set (empty = not set). */
  const [moscow, setMoscow] = useState<MoscowLevel | ''>(issue?.moscow ?? '');
  /* RUN/CHANGE override (#435) — null means "follow the type". A value is kept only
     when it differs from the derived class; see the field's onChange below. */
  const [invClass, setInvClass] = useState<InvestmentClass | null>(issue?.investmentClass ?? null);
  /* THE BOARD'S FIRST LIVE TYPE, not the word "task" — #302. A board using
     Vinnie's four has no `task` slug at all, and 0072's foreign key would
     refuse every card raised with it. */
  const [kind, setKind] = useState<IssueKind>(
    issue?.kind ?? liveTypes(useApp.getState().issueTypes)[0]?.slug ?? 'task'
  );
  /* '' is "no area", which is a real answer and the one most cards give. */
  const [area, setArea] = useState<string>(issue?.area ?? '');
  /* '' is "no phase" — #377, and like area, the answer most cards give. */
  const [phase, setPhase] = useState<string>(issue?.phase ?? '');
  const [due, setDue] = useState<string>(issue?.dueDate ?? '');
  const [start, setStart] = useState<string>(issue?.startsOn ?? '');
  // Hold Save while either date box holds an entry it could not read (see
  // useInvalidDates): the card is where the manual-date bug bit, so the block
  // starts here.
  const badDates = useInvalidDates();
  const areas = useApp((s) => s.areas);
  const boardLabels = useApp((s) => s.labels);
  const createArea = useApp((s) => s.createArea);
  const createLabel = useApp((s) => s.createLabel);
  const phasesAll = useApp((s) => s.phases);
  const issueTypes = useApp((s) => s.issueTypes);
  /*
   * THE LIVE ONES, PLUS THIS CARD'S OWN EVEN IF IT HAS BEEN RETIRED.
   *
   * Without the second half, opening a card whose area was retired would show
   * the box reading "No area" — and saving anything else on that card would
   * then quietly clear an area nobody meant to touch. A retired area is not on
   * offer for new work; it is still the truth about the work that has it.
   */
  const offerable = useMemo(() => {
    const live = liveAreas(areas);
    const mineNow = areas.find((a) => a.id === area);
    return mineNow && mineNow.retiredAt ? [...live, mineNow] : live;
  }, [areas, area]);
  // THE MISUSE NUDGE — when what has been typed into the Epic field is really one
  // of the board's AREAS (Network, Security…), offer to file it under the area
  // instead. An epic is a push of work with a finish line; a standing domain is
  // an area, and the whole reason people reach for the epic field is that it is
  // the easy one to type into. Soft and one-click — never blocks a real epic that
  // merely shares a name, and only shown when it is not already filed there.
  const epicLooksLikeArea = useMemo(
    () => (epic.trim() ? areaForText(epic, areas) : undefined),
    [epic, areas],
  );
  // THE FIELD-MISUSE NUDGE — what has been typed into Epic or Area is really a
  // value the card ALREADY has a field for (a Status, Type, Size, Priority or a
  // date). Offer to put it where it belongs in one click and clear it off the
  // epic/area. Warn, never block (Vinnie, 20 Oct); a date is warn-only, since it
  // has no single target (Start vs Due). `area` is stored as an id, so the area
  // side resolves it to the typed name first.
  const typeNames = useMemo(() => liveTypes(issueTypes).map((t) => t.name), [issueTypes]);
  const epicMisfiled = useMemo(() => (epic.trim() ? misfiledAs(epic, typeNames) : null), [epic, typeNames]);
  const areaMisfiled = useMemo(() => {
    const a = areas.find((x) => x.id === area);
    return a ? misfiledAs(a.name, typeNames) : null;
  }, [area, areas, typeNames]);
  /** Put a misfiled value on the card field it belongs to (never called for a date). */
  const applyMisfiled = (m: Misfiled) => {
    if (m.field === 'status') setStatus(m.apply as IssueStatus);
    else if (m.field === 'priority') setPriority(m.apply as IssuePriority);
    else if (m.field === 'size') setSize(m.apply as IssueSize);
    else if (m.field === 'moscow') setMoscow(m.apply as MoscowLevel);
    else if (m.field === 'type') {
      const slug = liveTypes(issueTypes).find((t) => t.name.toLowerCase() === (m.apply ?? '').toLowerCase())?.slug ?? m.apply ?? '';
      setKind(slug);
    }
  };
  /* The same rule for phases: live ones, plus this card's own if it was retired,
     so opening an old card never silently clears the phase it ran in. */
  const offerablePhases = useMemo(() => {
    const live = livePhases(phasesAll);
    const mineNow = phasesAll.find((p) => p.id === phase);
    return mineNow && mineNow.retiredAt ? [...live, mineNow] : live;
  }, [phasesAll, phase]);
  /* A target date that falls AFTER the card's phase is due to end is a
     contradiction worth flagging (Track B, Vinnie 13 Sep): the card can't land
     on time without the phase slipping. Plain YYYY-MM-DD days compare by day. */
  const duePhase = phasesAll.find((p) => p.id === phase);
  const duePastPhase = !!(due && duePhase?.targetEnd && due > duePhase.targetEnd);
  /* HELD AS TEXT, not as a number. A number input mid-edit is briefly empty, and
     storing that as 0 would record "no effort at all" every time somebody
     cleared the box to retype it. */
  const [difficulty, setDifficulty] = useState(
    issue?.difficulty === undefined || issue?.difficulty === null ? '' : String(issue.difficulty)
  );
  // How far along the work is (0–100), as a string so the box can be blank. Nullable
  // like difficulty: '' saves as null (nothing recorded, no bar).
  const [prog, setProg] = useState(
    issue?.progressPct === undefined || issue?.progressPct === null ? '' : String(issue.progressPct)
  );
  // A NEW CARD PRE-FILLS BOTH SLOTS FROM THE CREATOR'S RESOLVED STANCE (two-part
  // assignee, Feature A): a manager lands in Accountable, a doer in Responsible,
  // per their company role + any board/person override (src/company-role.ts). On a
  // board an assistant runs, the assistant fills the doer slot. Nothing is born
  // unowned, and nothing is locked — both are only starting values, clear or change
  // them before saving. An existing card keeps whatever it has, including blank.
  const createDefault = () => {
    const s = useApp.getState();
    const me = (s.user?.email ?? '').toLowerCase();
    const prof = s.profiles[me];
    return cardCreateDefault({
      me,
      personStance: prof?.cardDefaultStance,
      companyRole: prof?.companyRole,
      boardDefault: s.meta.cardDefaults,
      soleAssistant: defaultAssignee(s.meta),
    });
  };
  const [assignee, setAssignee] = useState(() => (isNew ? createDefault().assignee : issue?.assignee ?? ''));
  const [responsible, setResponsible] = useState(() =>
    isNew ? createDefault().responsible : issue?.responsible ?? '',
  );
  // The card-face pile's "name a doer" ghost opens a Responsible picker in place.
  const [pickDoer, setPickDoer] = useState(false);
  /*
   * DOER CAPTURE, THE IN-CARD PATH (A2, F1). The board asks "who's doing this?"
   * when a card with no doer is dragged into In Progress — but a card is just as
   * often STARTED from inside its own modal, by changing its Status. That path
   * left the doer silently unset, the exact gap the capture exists to close. So
   * here the same moment — Status set to In Progress with no Responsible — opens
   * the card's own Responsible field and scrolls to it, demanding the doer rather
   * than nagging with a second popover (the field is already on screen). Asked
   * once per opening: skip it (pick nothing, click away) and it will not re-open.
   */
  const [doerAsk, setDoerAsk] = useState(false);
  const doerDismissed = useRef(false);
  const respRowRef = useRef<HTMLDivElement>(null);
  const [labels, setLabels] = useState<string[]>(issue?.labels ?? []);
  const [size, setSize] = useState<IssueSize | ''>(issue?.size ?? '');
  /*
   * WHICH SPRINT THIS IS IN — shown and changed on the card itself, not only on
   * the Sprints screen. A board working in fortnights where the fortnight is
   * invisible in the place people actually open work is a board where nobody
   * knows what they committed to.
   *
   * Held apart from the other fields because saving it is NOT an ordinary field
   * write: it goes through `putInSprint`, which stamps when the card joined and
   * records the move. Folded into the patch below it would save perfectly well
   * and quietly skip both — and spillover is visible nowhere else.
   */
  const [sprintId, setSprintId] = useState(issue?.sprint ?? '');
  const [checks, setChecks] = useState<Record<string, boolean>>(issue?.checks ?? {});
  /*
   * THE CARD'S OWN CHECKLIST (issue.checklist) — like `checks` above, it is held in
   * state seeded once and persists AS IT CHANGES, not on Save: a tick in the read
   * view has no Save button to wait for, and the number it drives should move the
   * instant a box is ticked. `commitChecklist` writes it straight through for an
   * existing card; for a new card there is no id yet, so it is only held here and
   * folded into the `createIssue` patch below. A keystroke in a row passes
   * persist=false (draft only) so the card is not written on every character — it
   * lands on blur.
   */
  const [checklist, setChecklist] = useState<ChecklistItem[]>(issue?.checklist ?? []);
  const commitChecklist = (next: ChecklistItem[], persist = true) => {
    const prevDone = checklist.filter((i) => i.done).length;
    const prevAllDone = checklist.length > 0 && prevDone === checklist.length;
    setChecklist(next);
    if (persist && !isNew && issue) updateIssue(issue.id, { checklist: next });
    // THE CHECKLIST MAY OFFER TO MOVE THE CARD ALONG as its boxes are ticked — both
    // offers gated by the board's checklist prompts (default on, admin-tuned in
    // Settings › Workflow). Only on a real tick (persist, not a draft keystroke on a
    // row's text), only on an existing card. A move to Done wins over a move to In
    // Progress when a single tick both starts and completes a one-item list.
    if (!persist || isNew || !issue) return;
    const prompts = checklistPromptsOf(meta.checklistPrompts);
    if (!prompts.on) return;
    const nextDone = next.filter((i) => i.done).length;
    const nextAllDone = next.length > 0 && nextDone === next.length;
    // LAST TICK → offer to finish. The completing transition only (not every tick),
    // and never when the card is already Done. No DoD condition: a 100% checklist
    // clears the Done gate on its own (done.ts doneAllowed, under the same
    // `onComplete` flag), so there is no short-cut-round-a-gate to withhold.
    if (prompts.onComplete && !prevAllDone && nextAllDone && status !== 'done') {
      void offerToFinish();
      return;
    }
    // FIRST TICK on a To-do card → offer to start it. The 0 → ≥1 transition only:
    // ticking the first step is the moment work actually began (Vinnie, 28 Oct).
    if (prompts.onFirstTick && status === 'todo' && prevDone === 0 && nextDone >= 1) {
      void offerToStart();
    }
  };
  // Ask once, through the shared confirm dialog, whether to move the card to Done now
  // that its checklist is fully ticked. On yes, move it straight away — the checklist
  // itself writes immediately, so the status move matches that model; on no, nothing
  // changes and the person can move it by hand whenever they like.
  const offerToFinish = async () => {
    if (!issue) return;
    const ok = await confirmDialog({
      title: 'Move this card to Done?',
      body: 'Every item on the checklist is ticked.',
      confirmLabel: 'Move to Done',
      cancelLabel: 'Not yet',
      tone: 'caution',
    });
    if (!ok) return;
    setStatus('done');
    updateIssue(issue.id, { status: 'done' });
  };
  // The counterpart on the first tick: offer to move a To-do card to In Progress now
  // that work on its checklist has begun. Same model as offerToFinish — a single
  // confirmed move, written straight through; declining changes nothing.
  const offerToStart = async () => {
    if (!issue) return;
    const ok = await confirmDialog({
      title: 'Move this card to In Progress?',
      body: 'You’ve started ticking its checklist.',
      confirmLabel: 'Move to In Progress',
      cancelLabel: 'Not yet',
      tone: 'caution',
    });
    if (!ok) return;
    setStatus('inprogress');
    updateIssue(issue.id, { status: 'inprogress' });
  };
  // ── THE CALMED PROPERTIES (card-detail cleanup) ───────────────────────────
  // An UNSET optional field recedes to a quiet "+ Label" add-chip; clicking one
  // promotes the field to its normal editable row for THIS open card only — a
  // local reveal, no data written until the person actually sets a value.
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const reveal = (f: string) => setRevealed((s) => (s.has(f) ? s : new Set(s).add(f)));
  // The counterpart to reveal: send an optional field back to a "+" chip. It clears
  // the field's value AND drops it from the revealed set, so `shows(f)` goes false
  // and the row glides out (Collapsible). This is the "delete the field" that was
  // missing — once Area/Epic/Dates/… were added there was no way to take them off
  // again (Vinnie, 28 Oct). Mandatory fields (Status, Priority, Type) are never
  // removable, so they are not in here.
  const clearField = (f: string) => {
    switch (f) {
      case 'area': setArea(''); break;
      case 'epic': setEpic(''); break;
      case 'phase': setPhase(''); break;
      case 'size': setSize(''); break;
      case 'moscow': setMoscow(''); break;
      case 'difficulty': setDifficulty(''); break;
      case 'sprint': setSprintId(''); break;
      case 'labels': setLabels([]); break;
      case 'dates': setStart(''); setDue(''); break;
      case 'progress': setProg(''); break;
    }
  };
  const unreveal = (f: string) => {
    clearField(f);
    setRevealed((s) => {
      if (!s.has(f)) return s;
      const n = new Set(s);
      n.delete(f);
      return n;
    });
  };
  // The quiet "remove this field" control — a × that surfaces on row hover/focus and
  // sends the field back to its "+" chip. Only on an editable card.
  const RemoveX = ({ f, label }: { f: string; label: string }) =>
    canEditIssue ? (
      <button
        type="button"
        className="prop-x"
        aria-label={`Remove ${label}`}
        title={`Remove ${label}`}
        onClick={() => unreveal(f)}
      >
        ×
      </button>
    ) : null;
  // EVERY EDIT-MODAL SECTION FOLDS ON THE ONE AFFORDANCE (card-modal-collapse v2,
  // 3.29.0). Each section is a tri-state like Classification already was: its open
  // state is a hand-set toggle (null = follow the content-aware default below). The
  // choice is per open card and is NOT persisted — a null initial on every mount
  // means a hand-fold resets when the card reopens (no DB, no localStorage). "What's
  // blocking this" is the one section that never folds (Vinnie's locked decision).
  const [statusToggled, setStatusToggled] = useState<boolean | null>(null);
  const [checklistToggled, setChecklistToggled] = useState<boolean | null>(null);
  const [classToggled, setClassToggled] = useState<boolean | null>(null);
  const [dodToggled, setDodToggled] = useState<boolean | null>(null);
  const [summaryToggled, setSummaryToggled] = useState<boolean | null>(null);
  const [detailToggled, setDetailToggled] = useState<boolean | null>(null);
  const [codeToggled, setCodeToggled] = useState<boolean | null>(null);
  const [historyToggled, setHistoryToggled] = useState<boolean | null>(null);
  // FIELD-TARGETED OPEN (the non-summary half of openEditField) — place here, where
  // the section toggles are in scope: it opens the section that holds the target
  // field, then arms the focus effect above. Status, Responsible and the planned
  // dates live in "Status & Ownership"; Size and Sprint in "Classification".
  useEffect(() => {
    if (!openEditField || openEditField === 'summary') return;
    if (isNew || !canEditIssue) { clearOpenEditField(); return; }
    setEditing(true);
    if (openEditField === 'size' || openEditField === 'sprint') setClassToggled(true);
    else setStatusToggled(true);
    // The dates, size and sprint fields can sit behind a "+" chip on a card that has
    // none set — and a set-dates/set-size/add-to-sprint fix is exactly that card — so
    // reveal the field before landing on it, or there would be nothing to focus.
    if (openEditField === 'dates' || openEditField === 'size' || openEditField === 'sprint') reveal(openEditField);
    setFocusFieldWanted(openEditField);
    clearOpenEditField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditField]);
  const meta = useApp((s) => s.meta);
  // The workspace working calendar (0216) — so the card's progress-vs-schedule read
  // counts working days (weekends and the workspace's holidays don't count against it).
  const workCalendar = useApp((s) => s.workCalendar);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  // The lifecycle an untyped card's Run/Change class falls back to — the SAME tier the
  // Portfolio and the board Metrics strip use, so this picker reads a card the same way the
  // rollups count it (Q3/F7). Only consulted for a card whose type gives no signal.
  const boardIsProject = useApp((s) => isProjectMode(s.meta));
  const boardInService = useApp((s) => isInService(s.meta));
  const invLifeDefault = useMemo(() => {
    const proj = projects.find((p) => p.id === projectId);
    const tracked = !!(proj?.heldAt || proj?.goLive != null || livePhases(phasesAll).length > 0 || proj?.budget != null);
    return investmentLifecycleDefault(boardLifecycleTier({ tracked, atService: boardInService || !boardIsProject }));
  }, [projects, projectId, phasesAll, boardInService, boardIsProject]);
  const milestones = useApp((s) => s.milestones);
  const groupsOf = useApp((s) => s.groupsOf);
  /* Only people who can OPEN this board may be offered as an assignee — handing a
     card to somebody who cannot see it assigns work nobody can pick up. Same test
     the switcher uses (boardAssignees → canOpenProject). An assignee ALREADY on
     the card is kept selectable below even when off this list, so opening an old
     issue never silently unassigns it. */
  const assignable = useMemo(
    () => boardAssignees(projects.find((p) => p.id === projectId), access.emails, access.admins, groupsOf),
    [projects, projectId, access, groupsOf]
  );
  /** Whoever an issue is assigned to, person or assistant, by name. */
  const whoIs = (who: string) => {
    const bot = assigneeAssistant(who, meta);
    return bot ? bot.name?.trim() || bot.id : displayName(who, profiles);
  };

  /*
   * WHAT'S BLOCKING THIS — the list, authored in <BlockerSection> (#456).
   *
   * The whole typeahead-and-chip machinery that used to live here now lives in
   * that component, which takes `blockerEntries` and emits the next list. Two
   * things it needs from the modal: who the viewer is, stamped onto each entry so
   * the feed can name them; and which people the SAVED card already told, so a
   * chip reads "Told", not "Will be told". People come from `assignable`
   * (canOpenProject), the same seam as the Assignee field.
   */
  const me = useApp((s) => s.user?.email ?? '');
  const notifiedEmails = useMemo(() => {
    const set = new Set<string>();
    for (const e of issue?.blockerRefs ?? []) {
      for (const r of e.refs ?? []) {
        if (r.kind === 'person' && r.ref) set.add(r.ref.toLowerCase());
      }
    }
    return set;
  }, [issue?.blockerRefs]);

  const required = meta.doneChecks ?? [];
  // The REAL DoD state, for the readiness display — never relaxed, so the panel
  // always tells the truth about which shared checks are still open.
  const missing = outstandingChecks({ checks }, meta);
  // Whether THIS card can be done on its own checklist in place of the DoD — the
  // board allows checklist completion (prompts on + onComplete) and every item is
  // ticked. When true, a DoD with open checks no longer BLOCKS the card, so the
  // readiness copy softens from "can't be done yet" to a plain count.
  const doneViaChecklist =
    (() => {
      const p = checklistPromptsOf(meta.checklistPrompts);
      return p.on && p.onComplete && checklistComplete({ checklist });
    })();
  // True while anything is typed-but-unsent in the card's feed (an update, a
  // reply, or an answer to the reporter). The feed owns those drafts, and tells
  // us so closing can warn rather than bin them (ledger L54) and a reload holds
  // off (#268). A boolean, flipped only as the draft appears or clears.
  const [feedDraft, setFeedDraft] = useState(false);

  /*
   * SO THE UPDATE OFFER KNOWS NOT TO RELOAD OVER THIS — #268.
   *
   * Editing a card, or having something typed into the feed and not sent, is the
   * case where a reload costs somebody real work. Nothing anywhere could answer
   * "is anything unsaved", because edits live in component state — so the two
   * places that hold unsaved text say so, and the Update button asks.
   *
   * Editing rather than "changed from what it was": a field can be edited back
   * to its original value and the person is still mid-task, and comparing every
   * field on every keystroke to decide that would be worse in every way.
   */
  const unsaved = editing || feedDraft;
  useEffect(() => (unsaved ? holdingUnsaved() : undefined), [unsaved]);

  // The MANUAL % held as a string so the box can be blank; '' means "no number
  // typed", which is NOT 0 — a blank card counts from the checklist instead.
  const progN = prog.trim() === '' ? null : Math.max(0, Math.min(100, Math.round(Number(prog))));
  // The card's EFFECTIVE % and where it came from: the typed value when set, else
  // derived from the card's OWN checklist, else from the board's ticked Definition
  // of Done (progress-derive.ts). The bar and the health chip read the effective %;
  // the INPUT stays bound to progN, so leaving it blank keeps the card counting
  // from whichever list is driving it.
  const progEff = effectiveProgress({ progressPct: progN, checks, checklist }, meta.doneChecks);
  // Show the Progress row when a % is typed OR the card is counting from a list
  // (its own checklist, or the DoD ticks), so a list-tracked card surfaces its
  // derived bar rather than hiding behind "+ Progress".
  const progDerived = progN == null && progEff.pct != null;
  // The card's own-checklist figure, kept apart from progEff: when a manual % is
  // typed it OVERRIDES the checklist, so progEff reads the typed number — but the
  // override helper still needs to say what the checklist WOULD read (N of M done
  // = X%) and offer one click back to it (Finding A, the overridden state in the
  // approved mock). Counts come from the live checklist, so they track ticks.
  const clDone = checklist.reduce((n, it) => n + (it.done ? 1 : 0), 0);
  const clTotal = checklist.length;
  const clPct = clTotal > 0 ? Math.round((clDone / clTotal) * 100) : 0;

  // Which optional fields carry a value right now — a SET field is a row, an
  // UNSET one recedes to a "+ add" chip unless it has been revealed this open.
  // Type always shows and Investment always derives, so neither is optional.
  const fieldSet: Record<string, boolean> = {
    dates: start.trim() !== '' || due.trim() !== '',
    progress: prog.trim() !== '' || progDerived,
    checklist: checklist.length > 0,
    area: area.trim() !== '',
    epic: epic.trim() !== '',
    phase: phase !== '',
    size: size !== '',
    moscow: moscow !== '',
    labels: labels.length > 0,
    difficulty: difficulty.trim() !== '',
    sprint: sprintId !== '',
  };
  const shows = (f: string) => fieldSet[f] || revealed.has(f);
  // Classification's optional fields drive its fold and its count (Sprint only
  // on a board that runs in sprints).
  const classOptional = ['area', 'epic', 'phase', 'size', 'moscow', 'labels', 'difficulty', ...(inSprints ? ['sprint'] : [])];
  const classSetN = classOptional.filter((f) => fieldSet[f]).length;
  const classHasSet = classSetN > 0;
  const classRevealed = classOptional.some((f) => revealed.has(f));
  // Collapsed by DEFAULT even when classification fields are set — a data-rich
  // card's edit body otherwise overflows and scrolls. The collapsed header still
  // shows the "N set" count, so nothing is hidden; it opens when the user toggles
  // it or reveals a "+ add" field (classRevealed).
  const classOpen = classToggled ?? classRevealed;
  // CONTENT-AWARE FOLD DEFAULTS for the rest of the edit-modal sections (v2 spec).
  // Each is `hand-toggle ?? default`; the default reads live card data so a card
  // opens showing what it has to say and hiding what it does not.
  //   · Status & Ownership — the essentials, open always.
  //   · Checklist — open when it has items (or was just revealed by the +Checklist
  //     chip, so the composer is not hidden behind a fold).
  //   · Before this can be done (DoD) — open while a gate is unmet, folded at "all met".
  //   · What is this about? (summary) — open; it is the primary glance.
  //   · More Detail — folded; it runs long (matches the read-mode L150 decision).
  //   · Code changes / History — folded; the long tail, one tap away.
  const statusOpen = statusToggled ?? true;
  const checklistOpen = checklistToggled ?? (checklist.length > 0 || revealed.has('checklist'));
  const dodOpen = dodToggled ?? missing.length > 0;
  const summaryOpen = summaryToggled ?? true;
  const detailOpen = detailToggled ?? false;
  const codeOpen = codeToggled ?? false;
  const historyOpen = historyToggled ?? false;
  // The live progress health, computed once so the single calm line and the
  // field's (i) read from the same source. The full reasoning that used to fill
  // three extra lines now lives in the control's tooltip and the (i). It reads the
  // EFFECTIVE % (manual, else checklist-derived) so the bar is honest either way.
  // A card with no date of its own falls back to the window of the phase it is in
  // (the one being edited), so a phased, date-less card is judged, not left tracking.
  const progPhase = phase ? phasesAll.find((x) => x.id === phase) : undefined;

  // ONE-TAP ASSISTS — the board offers the size and due date the person did not fill
  // in, worked out from cards like this one (src/suggest.ts). Both read the LIVE
  // edited values (epic, phase, start, size, due) so the suggestion tracks what is on
  // screen, not the saved card. The suggesters are confidence-gated and return null
  // when they cannot stand behind a value, so the strip simply does not appear then.
  // Only offered on a card that is still being shaped — not when editing is barred,
  // and not once the person has waved that field's strip away this session.
  // (The per-board opt-out lives with the other board settings — project-idle-nudge.)
  const assistCard = useMemo(
    () =>
      issue
        ? { id: issue.id, kind: issue.kind, epic: epic || undefined, phase: phase || undefined, size: size || undefined, status, startsOn: start || null, dueDate: due || null }
        : null,
    [issue, epic, phase, size, status, start, due],
  );
  // An admin can switch the card assists off for this board (configureAssists,
  // 0205); absent reads as on. When off, no suggestion is computed or shown.
  const cardAssistsOn = meta.cardAssists !== false;
  const sizeSuggestion = useMemo(
    () => (cardAssistsOn && assistCard && mine('size') ? suggestSize(assistCard, allIssues) : null),
    [cardAssistsOn, assistCard, allIssues],
  );
  const cardSprint = useMemo(
    () => (sprintId ? sprints.find((s) => s.id === sprintId) ?? null : null),
    [sprintId, sprints],
  );
  // The card's doer (Responsible, else assignee) — whose own pace a personal due date
  // is a promise about. Empty when no one owns it yet (then source 0 stays silent and
  // the board-shaped sources answer). The learning pool is this board's cards for now;
  // it widens to cross-board history as the portfolio read does (Vinnie, 31 Oct).
  const dueOwner = issue ? ownerOf(issue) : '';
  const dueSuggestion = useMemo(
    () =>
      cardAssistsOn && assistCard && mine('dueDate')
        ? suggestDue(
            assistCard,
            progPhase ? { name: progPhase.name, targetEnd: progPhase.targetEnd } : null,
            allIssues,
            cardSprint ? { name: cardSprint.name, endsOn: cardSprint.endsOn } : null,
            Date.now(),
            // Always supply the history so the comparable cycle-time read (piece C) can
            // answer even when the card has no owner yet; the personal read (piece D)
            // simply skips when `owner` is empty.
            { owner: dueOwner, ownerLabel: dueOwner ? displayName(dueOwner, profiles) : undefined, history: allIssues, cal: workCalendar },
          )
        : null,
    [cardAssistsOn, assistCard, progPhase, allIssues, cardSprint, dueOwner, profiles, workCalendar],
  );
  // A blank field recedes to a "+ add" chip, which is exactly when a suggestion is
  // wanted — so a confident suggestion REVEALS the field (the picker and the strip
  // together, one tap to accept or pick by hand), and waving the strip away lets the
  // field recede again. `shows(...)` already covers the case where the field is set.
  const sizeAssistKey = issue ? `${issue.id}:size` : '';
  const showSizeAssist = !!sizeSuggestion && !!issue && !assistDismiss.includes(sizeAssistKey);
  const dueAssistKey = issue ? `${issue.id}:due` : '';
  const showDueAssist = !!dueSuggestion && !!issue && !assistDismiss.includes(dueAssistKey);

  // RELEASE OUTLOOK (Layer B · piece A) — the chance THIS card is finished by the go-live,
  // read from the board's own weekly pace and how deep the card sits in the queue (the same
  // Monte-Carlo resample the Forecast panel runs, so the two can never disagree). Resolves the
  // go-live exactly like that panel (goLiveTargetMs). Returns null — and the row simply does
  // not render — for a new card, a closed or blocked card, a board with no go-live, or one with
  // too little finished history to resample. Backlog counts as remaining work on a project
  // (the delivery-mode rule the forecast uses), not on a continuous board.
  // Who may see the outlook on this board: a Project Manager and above by default, or everyone
  // when an admin has opened it (showOddsToEveryone, 0220). The one gate shared with the sprint
  // strip (src/permissions.ts canSeeCompletionOdds) so visibility never drifts between surfaces.
  const maySeeOdds = canSeeCompletionOdds(role, showOddsToEveryone);
  const releaseChance = useMemo(() => {
    if (!issue || !maySeeOdds) return null;
    const project = projects.find((p) => p.id === projectId);
    const goLive = goLiveTargetMs(
      project,
      livePhases(phasesAll),
      (id) => milestones.find((m) => m.id === id)?.targetDay,
    );
    const countBacklog = project?.deliveryMode !== 'continuous';
    return releaseOutlook(issue, allIssues, goLive, Date.now(), projectId ?? '', workCalendar, countBacklog);
  }, [issue, maySeeOdds, projects, projectId, phasesAll, milestones, allIssues, workCalendar]);

  const progH = progressHealth({
    progressPct: progEff.pct,
    startsOn: start || progPhase?.startsOn || null,
    dueDate: due || progPhase?.targetEnd || null,
    startedAt: issue?.startedAt ?? null,
    createdAt: issue?.createdAt,
    resolved: status === 'done' || status === 'wontdo',
  }, Date.now(), workCalendar);
  const progShowTick = progH.expectedPct != null && progH.kind !== 'tracking';
  const progReason =
    progH.kind === 'none'
      ? ''
      : progH.kind === 'tracking'
        ? 'No due date — set one to track against the calendar.'
        : [progH.reason, progShowTick ? `Calendar expects about ${progH.expectedPct}% by now.` : '']
            .filter(Boolean)
            .join(' ');
  // Asked once as the card opens so the feed knows whether to offer the answer-
  // the-reporter box; the feed reads the resolved reporter from the store.
  const loadReporter = useApp((s) => s.loadReporter);
  const [showMissing, setShowMissing] = useState(false);
  /*
   * Reading the notes back as they will appear, without leaving the form.
   *
   * The notes are the one field written as markdown with references in it —
   * "queued behind #200" — and in read mode those become links. While editing
   * they are raw text in a box, so the only way to check a reference resolved
   * was to save and look, then edit again to fix it.
   */
  const [preview, setPreview] = useState(false);
  /*
   * The title box grows with its text.
   *
   * A textarea keeps whatever height it was given, so a title that wraps onto a
   * second line would sit behind a scrollbar. Measured after every change, and
   * once when the form opens — an existing title is already long before
   * anybody types a character.
   */
  const titleBox = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = titleBox.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title, editing]);
  /*
   * Asked once as the card opens. On every board but the support one the
   * answer is "nobody" for every card, so it is asked here rather than loaded
   * for the whole board — see loadReporter, which remembers the misses too.
   * The feed reads the resolved reporter to decide whether to offer the answer box.
   */
  useEffect(() => {
    if (issue?.id) void loadReporter(issue.id);
  }, [issue?.id, loadReporter]);

  /* Clear the decision-seed field when a different card opens — it always starts
     empty; the blocker's reason is shown read-only beside it, not pre-filled. */
  useEffect(() => {
    setDecidedText('');
  }, [issue?.id]);

  /**
   * Escape closes the card.
   *
   * The test script has told people to press it since the script was written,
   * and it never worked — the only Escape handler on the screen belonged to the
   * name picker. Clicking outside already closes the card, so this is the same
   * decision reached with the keyboard.
   *
   * The picker gets first refusal while it is open. Closing the whole card
   * instead would throw away the comment being written, which is the opposite
   * of what somebody pressing Escape to dismiss a list of names wants.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      /*
       * Command-Enter saves.
       *
       * The save button sits at the bottom of a card that scrolls, so on a long
       * issue somebody typing in the notes has to stop, scroll, and aim. Bound
       * on the window rather than the form because the last thing touched is a
       * textarea, where Enter is a newline and must stay one.
       *
       * Only while editing, and only when there is a title — the same condition
       * the button itself is disabled by, so the keyboard cannot do what the
       * button refuses.
       */
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        submit.current();
        return;
      }
      /*
       * E starts editing.
       *
       * A bare letter, so it must not fire while somebody is typing one. The
       * check is on where the key landed rather than on a flag we keep: a flag
       * goes stale the moment a new field is added and nobody remembers to set
       * it.
       */
      if (e.key === 'e' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const el = e.target as HTMLElement | null;
        const typing =
          el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable;
        if (!typing) {
          e.preventDefault();
          edit.current();
        }
        return;
      }
      if (e.key !== 'Escape') return;
      /*
       * Escape backs out of whatever you are in.
       *
       * It used to close the card outright, even mid-edit — so labelling Cancel
       * with "Esc" would have been a lie, since Cancel only leaves the form.
       * Now the two agree: editing, it returns you to reading; reading, it
       * closes. Backing out one step at a time is what people expect of it, and
       * it is the less destructive of the two when a form is open.
       *
       * Deferred a tick, not called inline: when backing out reaches a confirm
       * (a half-written comment not posted), the in-app confirm binds its own
       * Escape listener as it mounts. Letting this key event finish first means
       * the same press can't both open the confirm and immediately dismiss it —
       * the belt-and-braces for the old WebKit window.confirm flash, kept now
       * that the confirm is the app's own. The step itself is unchanged.
       */
      setTimeout(() => void leave.current(), 0);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeModal]);

  /*
   * Held in a ref, not in the effect's dependencies.
   *
   * `save` is rebuilt every keystroke because it closes over every field, and
   * `save` itself is declared below an early return, so the effect cannot simply
   * depend on it. Listing it would also mean tearing down and re-attaching the
   * key handler on every character typed. Reading it through a ref means the
   * shortcut always runs the current one — the alternative being a shortcut that
   * saves whatever the form held when the card was opened.
   */
  const submit = useRef<() => void>(() => {});
  const edit = useRef<() => void>(() => {});
  const leave = useRef<() => void>(() => {});

  if (!isNew && !issue) return null;

  // Refreshed every render, so the shortcuts and the buttons can never disagree.
  submit.current = () => {
    if (editing && title.trim()) save();
  };
  edit.current = () => {
    if (!isNew && !editing && canEditIssue) setEditing(true);
  };
  /*
   * WHETHER THE OPEN EDIT FORM HOLDS CHANGES NOT YET SAVED TO THE CARD.
   *
   * Leaving the form by Escape or Cancel used to step straight back to reading with
   * no question, while the close cross asked first — so whether an unsaved edit was
   * protected depended on which exit you picked, and after Escape the read view
   * showed the saved values with no sign an edit was still pending (a later close
   * then dropped it silently). `leave` now asks the same discard question the cross
   * does, but only when there is really something to lose, so a clean edit still
   * steps back without a pointless prompt. Compares the BUFFERED fields to the saved
   * card; `sprint` and `checks` are left out on purpose — they save as they are
   * changed, not on Save, so they are not part of this buffer. A field missed here
   * only falls back to the old quiet step-back for that one field — never a silent
   * loss — but keep it in step with the field initialisers above.
   */
  const editDirty =
    editing && !isNew && !!issue && (
      title !== (issue.title ?? '') ||
      summary !== (issue.summary ?? '') ||
      description !== (issue.description ?? '') ||
      JSON.stringify(blockerEntries) !== JSON.stringify(blockersOf(issue)) ||
      epic !== (issue.epic ?? '') ||
      status !== issue.status ||
      priority !== (issue.priority ?? 'medium') ||
      moscow !== (issue.moscow ?? '') ||
      invClass !== (issue.investmentClass ?? null) ||
      kind !== issue.kind ||
      area !== (issue.area ?? '') ||
      phase !== (issue.phase ?? '') ||
      due !== (issue.dueDate ?? '') ||
      start !== (issue.startsOn ?? '') ||
      difficulty !== (issue.difficulty === undefined || issue.difficulty === null ? '' : String(issue.difficulty)) ||
      prog !== (issue.progressPct === undefined || issue.progressPct === null ? '' : String(issue.progressPct)) ||
      assignee !== (issue.assignee ?? '') ||
      responsible !== (issue.responsible ?? '') ||
      labels.join('\u0001') !== issue.labels.join('\u0001') ||
      size !== (issue.size ?? '')
    );
  /*
   * Put the buffered fields back to the saved card, so leaving the form is a REAL
   * discard: the read view shows the truth and re-opening the editor starts clean,
   * rather than resurfacing edits the person just chose to drop (and asking to
   * discard them all over again). The same field set as `editDirty` — sprint and
   * checks are left alone on purpose, they save as they change, not on Save.
   */
  const resetEdits = () => {
    if (!issue) return;
    setTitle(issue.title ?? '');
    setSummary(issue.summary ?? '');
    setDescription(issue.description ?? '');
    setBlockerEntries(blockersOf(issue));
    setEpic(issue.epic ?? '');
    setStatus(issue.status);
    setPriority(issue.priority ?? 'medium');
    setMoscow(issue.moscow ?? '');
    setInvClass(issue.investmentClass ?? null);
    setKind(issue.kind);
    setArea(issue.area ?? '');
    setPhase(issue.phase ?? '');
    setDue(issue.dueDate ?? '');
    setStart(issue.startsOn ?? '');
    setDifficulty(issue.difficulty === undefined || issue.difficulty === null ? '' : String(issue.difficulty));
    setProg(issue.progressPct === undefined || issue.progressPct === null ? '' : String(issue.progressPct));
    setAssignee(issue.assignee ?? '');
    setResponsible(issue.responsible ?? '');
    setLabels(issue.labels ?? []);
    setSize(issue.size ?? '');
  };

  /*
   * ENTERING EDIT STARTS FROM THE CURRENT CARD, NOT THE MOUNT SNAPSHOT (N1).
   *
   * The buffered fields above are seeded once, when the modal mounts. If the card
   * changes WHILE THE MODAL IS OPEN — most easily by moving its status from the
   * feed composer in read mode — those buffers go stale: the read header shows the
   * new status (it reads the issue directly) but the edit form still holds the old
   * one, and Save would write the stale value back, silently reverting the move.
   * So re-seed from the live issue on the read→edit transition (never while already
   * editing, which would throw away work in progress). resetEdits is a no-op for a
   * new card (no issue), so an isNew card that opens straight into edit is untouched.
   */
  const wasEditing = useRef(editing);
  useEffect(() => {
    if (editing && !wasEditing.current) resetEdits();
    wasEditing.current = editing;
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps -- seed only on the edit-entry edge
  /*
   * A STRAY CLICK OR ESCAPE MUST NOT THROW AWAY WORK IN PROGRESS — #417.
   *
   * The backdrop, the close cross and Escape all end the card, and the card's
   * fields and the half-written comment live only in this component's state, so
   * ending it is where they are lost. Ask first when there is something to lose:
   * an existing card in edit mode, a new card with anything typed into it, or a
   * comment drafted and not posted. An untouched new card, or a card only being
   * read, closes straight through — a prompt with nothing behind it is noise.
   *
   * Save and delete close by calling dismiss() directly: they have already kept
   * what matters, so they never meet this question.
   */
  const askThenDismiss = async () => {
    const draft = feedDraft;
    const typedNew =
      isNew && (title.trim() !== '' || summary.trim() !== '' || description.trim() !== '');
    // Ask only when there is really something to lose: a dirty edit (a clean edit
    // closes straight through, as leaving the form does) or a new card with text.
    const losingEdit = editDirty || typedNew;
    if (losingEdit || draft) {
      const what =
        losingEdit && draft
          ? 'your unsaved changes and what you have not posted to the feed'
          : losingEdit
            ? 'your unsaved changes to this card'
            : 'what you have not posted to the feed';
      const ok = await confirmDialog({
        title: 'Discard your changes?',
        body: `You will lose ${what}.`,
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'caution',
      });
      if (!ok) return;
    }
    dismiss();
  };
  // Both the in-form Cancel button and Escape come through here: stop editing and
  // step back to reading (asking first if the edit holds unsaved changes), or,
  // when already reading, close the card. Backing out one step at a time — Escape
  // defers this a tick (see the keydown handler) so the WebKit discard-flash
  // doesn't bite; the ✕ and backdrop call askThenDismiss directly.
  leave.current = async () => {
    if (editing && !isNew) {
      // Ask before dropping unsaved edits; declining keeps you in the form so you
      // can Save. A clean edit steps back silently — nothing to lose.
      if (editDirty) {
        const ok = await confirmDialog({
          title: 'Discard your changes?',
          body: 'Your unsaved changes to this card will be lost.',
          confirmLabel: 'Discard',
          cancelLabel: 'Keep editing',
          tone: 'caution',
        });
        if (!ok) return;
      }
      resetEdits();
      setEditing(false);
    } else void askThenDismiss();
  };

  const save = async () => {
    if (!title.trim()) return;
    // A date box holds something it could not read. Read it from the ref, not the
    // disabled state: clicking Save blurs the box, which commits and flags in the
    // same tick, before the button's disabled re-render lands. Nothing to show —
    // the field already carries its own red note.
    if (badDates.ref.current) return;
    // A blocker OR a dependency the person built but did not press "Add" on is
    // folded into what we write, rather than silently dropped. Each flushPending
    // returns its section's slice (and keeps the working copy in step); null means
    // nothing was pending there. Because the two sections each hold only one kind of
    // ref, the written list is the other-blockers slice followed by the card-deps
    // slice — flushed where a draft was pending, projected from the working copy
    // where it was not. When NEITHER had a draft we write the working copy whole, so
    // an untouched legacy entry that still mixes a person and a card is left intact.
    const flushedOthers = blockerRef.current?.flushPending();
    const flushedDeps = depRef.current?.flushPending();
    const effectiveBlockers = flushedOthers || flushedDeps
      ? [...(flushedOthers ?? otherBlockersOf(blockerEntries)), ...(flushedDeps ?? cardDepsOf(blockerEntries))]
      : blockerEntries;
    // Refuse here as well as on the board: the status dropdown is the other way
    // into Done, and a gate with a way round it is not a gate. A fully-ticked card
    // checklist clears the gate in place of the DoD (doneAllowed), so a card may be
    // done either by meeting the shared checks or by finishing its own list.
    if (status === 'done' && !doneAllowed({ checks, checklist }, meta)) {
      setShowMissing(true);
      return;
    }
    /*
     * THE SOFT START-GATE — the status control is the second deliberate-start
     * surface (the Board's drag is the first). Moving a card into a started column
     * while it still waits on an unfinished predecessor WARNS, it does not refuse:
     * Cancel aborts the save (the form stays open, nothing is written); "Start
     * anyway" lets the save through and records the override on the card's history.
     * On an existing card only — a brand-new card has no predecessor state to gate.
     */
    let overrideLog: StatusUpdate[] | undefined;
    if (!isNew && issue && status !== issue.status) {
      const byRef = new Map(allIssues.map((c) => [c.ref, c] as const));
      // Gate against the blockers as the save will WRITE them (blockerEntries),
      // not only the persisted ones, so adding a blocker and starting the card in
      // the same save is still caught. The card's own status stays the FROM.
      const gateCard = { ...issue, blockerRefs: effectiveBlockers };
      const gate = await askStartGate(gateCard, status, byRef, displayName(me, profiles) || me, meta.depHardBlock ?? false);
      if (!gate.proceed) return; // cancelled — the status move, and the save, do not happen
      if (gate.overrideNote) {
        // The card's `auto` move line carries the override note instead of being
        // blank — the same line updateIssue would write for a bare move (it is
        // skipped there because we hand it a standsLog here). Still `auto`, so the
        // fan-out trigger raises no call-out.
        overrideLog = [
          ...(issue.standsLog ?? []),
          { id: crypto.randomUUID(), at: Date.now(), by: me, text: gate.overrideNote, status, auto: true },
        ];
      }
    }
    /*
     * THE LIST IS THE TRUTH; THE OLD SINGLE FIELDS ARE KEPT IN STEP (#456).
     *
     * A card now holds a LIST of blockers (`blockerRefs`), authored in the
     * section below. The old single fields — `blocked` / `blockerType` /
     * `blockerTarget` — are still written, mirrored from the FIRST entry, so
     * everything that still reads the scalar (the card-face badge, Next up, the
     * derive in blocked.ts) keeps working until each of those moves onto the
     * list. An empty list clears all four. "No" and the like never reach here:
     * the editor already dropped any entry that neither names nor says anything.
     */
    const primary = effectiveBlockers[0];
    const primaryCard = primary?.refs.find((r) => r.kind === 'card');
    const primaryPerson = primary?.refs.find((r) => r.kind === 'person');
    const mirrorText = primary
      ? primary.text.trim() ||
        (primaryCard
          ? `${primaryCard.ref}${primaryCard.label ? ` ${primaryCard.label}` : ''}`
          : primaryPerson
            ? `Waiting on ${primaryPerson.label || primaryPerson.ref}`
            : '')
      : '';
    const fields = {
      title: title.trim(),
      summary,
      blockerRefs: effectiveBlockers,
      blocked: mirrorText,
      blockerType: (primary ? primary.type || 'other' : '') as BlockerType | '',
      blockerTarget: primaryCard?.ref || primaryPerson?.label || primaryPerson?.ref || '',
      epic: epic.trim(),
      // `stands` is deliberately NOT written from here any more — the card's
      // "where it stands" box is gone and the Updates & comments feed owns the
      // standing note. Saving it from this form would let an edit undo whatever
      // the latest status update set.
      description,
      status,
      priority,
      /* NULL, NOT undefined, so clearing it back to "not set" actually travels —
         the same rule the fields below spell out. */
      moscow: moscow || null,
      /* NULL, NOT undefined, so clearing the override back to auto actually travels
         (same rule as moscow/area). `invClass` already holds only-the-difference:
         the field's onChange stores null whenever the pick equals the derived class. */
      investmentClass: invClass,
      kind,
      /*
       * NULL, NOT UNDEFINED, and for exactly the reason written out under
       * `difficulty` below: an undefined value is dropped by JSON.stringify, so
       * the column is never named and the write silently leaves it alone.
       * Taking an area back off a card has to travel as a value or it does not
       * happen at all.
       */
      area: area || null,
      // NULL, NOT UNDEFINED — the same reason as area above: taking a card back
      // to "No phase" has to travel as a value or the write leaves it alone.
      phase: phase || null,
      // The target date (Track B) — a plain YYYY-MM-DD day, and NULL not
      // undefined so clearing the field actually empties the column (same reason
      // as area/phase/difficulty).
      dueDate: due || null,
      startsOn: start || null,
      assignee,
      responsible,
      size,
      /*
       * THE TEXT BACK TO A NUMBER, and an empty box back to "nobody has said".
       * Null rather than 0, because the database and the document both treat
       * those as different answers — a card nobody has judged leaves the column
       * on the change request blank, and a card judged at nought prints one.
       *
       * NULL RATHER THAN UNDEFINED, which is the part that has to be written
       * down. An undefined value is not missing from the record, it is missing
       * from the REQUEST: `JSON.stringify` drops the key entirely, so the column
       * is never named, so the database leaves it exactly as it was. Emptying
       * the box and saving looked like it worked and changed nothing — the same
       * silent shape as a write addressed to no rows. Null is a value, so it
       * travels, so it clears.
       */
      difficulty: difficulty.trim() === '' ? null : Number(difficulty),
      progressPct: prog.trim() === '' ? null : Math.max(0, Math.min(100, Math.round(Number(prog)))),
      checks,
      // The card's own checklist. For an existing card every change already wrote
      // through commitChecklist as it happened; writing it again on Save is a
      // no-op. For a NEW card there was no id to write to, so this is where the
      // list the person built while creating the card first reaches the database.
      checklist,
      labels: labels
        .map((l) => l.trim())
        .filter(Boolean)
        .filter((l, i, a) => a.findIndex((x) => x.toLowerCase() === l.toLowerCase()) === i),
      // Only present on a soft-gate override: it carries the move's `auto` line
      // (with the override note) and so stands in for the one updateIssue writes.
      ...(overrideLog ? { standsLog: overrideLog } : {}),
    };
    if (isNew) {
      /*
       * A NEW CARD CAN BE BORN IN A SPRINT, and the join time is stamped with
       * it. No history entry: it was never anywhere else, and "pulled it into
       * Sprint 4" about a card that started there reads as a move that never
       * happened. `addedToSprintAt` is what committed-versus-added is counted
       * from, so it is the half that has to be written.
       *
       * Written here rather than through `putInSprint` because that needs an id
       * and `createIssue` does not hand one back — and adding a round trip to
       * fetch it would be a second write that can fail on its own.
       */
      createIssue(
        inSprints && sprintId
          ? { ...fields, sprint: sprintId, addedToSprintAt: Date.now() }
          : fields
      );
    }
    else {
      updateIssue(issue!.id, fields);
      /*
       * THE SPRINT MOVES SEPARATELY, and it has to. `putInSprint` stamps when
       * the card joined and writes the from/to on the history — the two things
       * committed-versus-added and spillover are made of, and neither is
       * recoverable afterwards. Folded into the patch above it would save the
       * column and skip both, which is the exact shape of bug this codebase has
       * shipped before: the write succeeds and the record silently does not.
       *
       * Only on a real change. `putInSprint` returns early on a no-op, but
       * asking here keeps a plain title edit from touching sprint history at
       * all.
       */
      if (inSprints && sprintId !== (issue!.sprint ?? '')) void putInSprint(issue!.id, sprintId);
    }
    dismiss();
  };

  /**
   * NO LONGER "CANNOT BE UNDONE", because it can — a copy goes into the trail
   * first and an admin can put it back from there. The wording said otherwise
   * for as long as it was true.
   *
   * If the copy cannot be written the issue is NOT deleted, and the reason is
   * said here rather than left as a button that appeared to do nothing.
   */
  const remove = async () => {
    if (!issue) return;
    const ok = await confirmDialog({
      title: `Delete ${issue.ref}?`,
      body: (
        <>
          “<b>{issue.title}</b>” — a copy is kept in People and access › History, so an admin can
          restore it.
        </>
      ),
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    void deleteIssue(issue.id).then(dismiss, (e: unknown) => {
      const why = e instanceof Error ? e.message : String(e);
      setDeleteError(
        /permission/i.test(why)
          ? 'Nothing was deleted — the record that would let it be restored could not be written, and it is not deleted without one.'
          : `Nothing was deleted — ${why}`
      );
    });
  };

  return (
    <div className={`overlay ${leaving ? 'is-leaving' : ''}`} onClick={askThenDismiss}>
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`modal ${leaving ? 'is-leaving' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Only when this card was reached from the Portfolio: the way back to
            exactly where we left. A breadcrumb above the header — distinct from
            the Edit / ESC controls, which change or close the card rather than
            navigate. The container carries the header's side padding because
            .modal itself has none (only head/body/actions do). */}
        {portfolioReturn && (
          <div className="modal-crumb">
            <button type="button" className="modal-back" onClick={askThenDismiss}>
              <Icon name="chevronLeft" /> Portfolio
            </button>
          </div>
        )}
        <div className="modal-head">
          {/* Reference and title on one line, as the mockup draws it — and in
              BOTH states, which is the point. Editing, the title IS the field:
              putting an editable copy anywhere else means two versions of the
              same sentence, one of them going stale as you type. */}
          <span className={`modal-ref${editing ? ' is-editing' : ''}`}>
            {isNew ? 'New issue' : issue!.ref}
            {/* The dash stands apart from the title so the title is a column of
                its own — that is what lets the second line start under the
                title rather than under the number. */}
            {!isNew && !editing && (
              <>
                <span className="modal-head-dash"> - </span>
                <span className="modal-head-title">{issue!.title}</span>
                {/* The "On Hold" state now reads as ONE red badge beside Resume in the
                    header actions (Vinnie, 25 Oct) — not here on the title and not again
                    on the Progress row. */}
              </>
            )}
            {editing && !isNew && <span className="modal-head-dash"> - </span>}
            {editing && (
              /* A textarea, not an input — an input cannot wrap, and a long
                 title would scroll sideways inside it instead of running onto a
                 second line the way it does when you are reading. It grows to
                 fit rather than showing a scrollbar. */
              <textarea
                ref={titleBox}
                className="modal-head-input"
                rows={1}
                placeholder="Issue title"
                disabled={!mine('title')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus={isNew}
              />
            )}
          </span>
          <div className="modal-head-actions">
            {/* ON HOLD — the single red badge for the state, beside the Resume button
                (Vinnie, 25 Oct). Same .rag r shape as every other badge; the reason,
                when one was given, rides in the tooltip. Shown to everyone viewing a
                held card, whether or not they can resume it. */}
            {!isNew && !editing && issue!.heldAt != null && (
              <span className="rag r modal-hold-badge" title={issue!.holdReason || undefined}>
                <span className="dot" />
                On Hold
              </span>
            )}
            {/* PUT ON HOLD / RESUME — one click, no required reason
                ([[feedback-flexible-not-rigid-for-adoption]]). Pausing stops the
                idle nudge and the clock; resuming moves the dates out by the time
                held and cascades that to dependents (store resumeIssue). Hold reads
                red (you are stopping the work), Resume reads green (you are starting
                it again) — Vinnie, 25 Oct. */}
            {!isNew && !editing && canEditIssue && (
              <button
                {...inert(`btn btn-small ${issue!.heldAt != null ? 'btn-resume' : 'btn-hold'}`, () =>
                  issue!.heldAt != null ? resumeIssue(issue!.id) : holdIssue(issue!.id),
                )}
              >
                {/* The ACTION is "Hold" — the state word "On Hold" is reserved for a
                    card that actually is (the badge), so a card being worked
                    never reads "On Hold" from its own controls. */}
                {issue!.heldAt != null ? 'Resume' : 'Hold'}
              </button>
            )}
            {!isNew && !editing && canEditIssue && (
              <button {...inert('btn btn-small', () => setEditing(true))}>
                Edit <span className="key-hint">E</span>
              </button>
            )}
            {/* The close cross has always answered Escape; it just never said
                so. A shortcut nobody is told about is a shortcut nobody uses.
                Reads ESC, as the mockup draws it, and it really is what Escape
                does here — the word is the control rather than a label stuck
                beside one. Escape now closes the card in both states (editing
                included, after the discard prompt), so it reads ESC either way. */}
            {/* Out of the tab order on purpose — tabbing through a card should
                move field to field, not stop on Close. Escape still closes it
                (and the label says so), and a mouse can always click it. */}
            <button
              className="modal-close"
              onClick={askThenDismiss}
              tabIndex={-1}
              aria-label={editing ? 'Close without saving (Escape)' : 'Close (Escape)'}
            >
              ESC
            </button>
          </div>
        </div>

        <div className="modal-body">
          {/* No title here, in either state — the header line carries it. */}

          {/* THE CARD FACE, EDITING VIEW — the doer + accountable pile, and when
              no doer is named yet a ghost "+" that opens the Responsible picker in
              place, so a doer can be set without hunting for the field below. In
              the read view the same face folds onto the status line instead (see
              .read-primary below), so it is not drawn twice. */}
          {editing && (
            <CardFace
              variant="block"
              assignee={assignee}
              responsible={responsible}
              meta={meta}
              profiles={profiles}
              assignable={assignable}
              editable={canEditIssue}
              pickDoer={pickDoer}
              setPickDoer={setPickDoer}
              setResponsible={setResponsible}
            />
          )}

          {/* The read view's blocker display — the SAME section as the editor,
              read-only: every blocker the card carries, each with its live card
              status and who's been told. Shown only when something is actually
              blocking (a card that reads "No" derives no entry, so this is empty
              and nothing draws — the old "a word that means nothing is not a
              blocker" rule, now in blockersOf). And it is gated on stillBlocked,
              not just on there being entries, so a card whose only blocker is a
              now-finished card stops reading "Blocked" here the way Next up
              already stopped treating it as blocked (L157). A part-cleared card
              still shows, every entry carrying its live [Done] status. */}
          {/* DEPENDS ON / BLOCKS — read-only, shown whenever the card has a card
              link or something waits on it (it self-hides when there is neither),
              independent of the "still blocked" banner. */}
          {!isNew && !editing && (
            <div className="read-blockers">
              <DependencySection
                entries={cardDepsOf(issue!.blockerRefs ?? [])}
                onChange={() => {}}
                allIssues={allIssues}
                self={{ ref: issue!.ref, status: issue!.status, startsOn: issue!.startsOn, dueDate: issue!.dueDate }}
                me={me}
                enforced={meta.depHardBlock === true}
                workCalendar={workCalendar}
                readOnly
              />
            </div>
          )}

          {!isNew && !editing && issue!.status !== 'done' && issue!.status !== 'wontdo' && stillBlocked(issue!, allIssues) && (
            <div className="read-blockers">
              <BlockerSection
                entries={otherBlockersOf(blockersOf(issue!))}
                onChange={() => {}}
                allIssues={allIssues}
                assignable={assignable}
                profiles={profiles}
                me={me}
                notifiedEmails={notifiedEmails}
                selfId={issue?.id}
                readOnly
              />
            </div>
          )}

          {/*
           * DERIVE-FIRST SEED (A) — a `decision`-type blocker IS a decision waiting
           * to be made; clearing it is the moment to capture what was decided. The
           * box sits directly under the Blocked banner, in reading mode where the
           * card is viewed (edit mode has its own blocker field + blk-x clear). The
           * primary logs the decision AND clears the blocker in one act; "Just clear
           * the blocker" stays the escape hatch — nobody is forced to log. Writer-
           * gated; the decisions RLS is the backstop. Mock 12.
           */}
          {!isNew && !editing && blockerEntries.some((e) => e.type === 'decision') && canEditIssue && (
            <div className="seedbox">
              <div className="seed-h">
                <NavIcon view="decisions" size={16} />
                What was decided?
              </div>
              <div className="seed-prefill">What this card was waiting on</div>
              <div className="seed-reason">
                {blockerEntries.find((e) => e.type === 'decision')?.text || 'A decision'}
              </div>
              <input
                className="ctl"
                style={{ marginTop: 10 }}
                aria-label="What was decided"
                placeholder="Type the decision that was made"
                value={decidedText}
                onChange={(e) => setDecidedText(e.target.value)}
              />
              <div className="seed-row">
                <button
                  type="button"
                  disabled={seedLogging}
                  {...inert('btn btn-primary btn-small', async () => {
                    setSeedLogging(true);
                    setSeedProblem('');
                    const err = await logDecision(
                      decisionFromBlocker({
                        issueId: issue!.id,
                        issueRef: issue!.ref,
                        issueTitle: issue!.title,
                        blockerText: decidedText.trim(),
                      })
                    );
                    setSeedLogging(false);
                    // Only clear the blocker once the decision is safely logged —
                    // otherwise the blocker would vanish with nothing recorded.
                    if (err) {
                      setSeedProblem(err);
                      return;
                    }
                    void clearDecisionBlocker();
                  })}
                >
                  <Icon name="check" size={14} />
                  Clear blocker &amp; log decision
                </button>
                <button
                  type="button"
                  disabled={seedLogging}
                  {...inert('btn btn-small', () => void clearDecisionBlocker())}
                >
                  Just clear the blocker
                </button>
                <span className="seed-note">
                  <Icon name="link" size={13} />
                  Links to {issue!.ref} automatically
                </span>
              </div>
              {seedProblem && (
                <p className="hintline" role="alert" style={{ marginTop: 8 }}>
                  {seedProblem}
                </p>
              )}
            </div>
          )}

          {/* The read-only summary — hidden while editing, where the same fields
              are the editable properties right below and a second copy above
              them only reads as clutter.

              ── THE PHASE-B RE-CUT ────────────────────────────────────────────
              This was a flat pile of chips (status, priority, type, size, checks,
              sprint, area, phase, epic, assignee, labels, builds) with no order
              to it, so the one screen you open to see what a card IS made you
              hunt. Now the triage line — where it stands, how urgent, who has it
              — sits on top, and everything the board face used to carry drops
              into a quiet labelled Details grid below it, read rather than
              hunted. Nothing left the card; it is just sorted. */}
          {!isNew && !editing && (
            <div className="read-facts">
              {/* The triage line — where it stands, how urgent, AND who has it:
                  the three facts a PM looks for first, kept together at the top
                  (#302). It used to carry only status and priority, leaving the
                  line four-fifths empty and the assignee stranded in the audit
                  footer; now identity sits where the eye lands. Raised / last
                  changed stay in the quiet footer (`.read-meta`) — reference,
                  not triage. */}
              <div className="read-primary">
                <span className={`status-pill dot-bg-${issue!.status}`}>
                  {STATUS_LABELS[issue!.status]}
                </span>
                <Priority priority={issue!.priority} />
                {/* WHO HAS IT — the two-part assignee folded onto the status line:
                    the doer and the Accountable person as one overlapping pile with
                    the plain-words line beside it (the doer in front and tinted).
                    This replaced the lone Accountable chip so "who" reads here once,
                    not twice (Vinnie, 22 Oct). The labelled fields in Details stay
                    the source of truth. */}
                <CardFace
                  variant="line"
                  assignee={issue!.assignee ?? ''}
                  responsible={issue!.responsible ?? ''}
                  meta={meta}
                  profiles={profiles}
                  assignable={assignable}
                  editable={false}
                  pickDoer={false}
                  setPickDoer={() => {}}
                  setResponsible={() => {}}
                />
              </div>

              <div className="read-details">
                <div className="read-details-h">Details</div>
                <dl className="read-grid">
                  {/* Type — always drawn, the whole word spelled out (labelOf, so
                      a renamed type reads true) and colour-coded by what the work
                      MEANS, led by a 16px mark: a star in the brand teal for
                      Building, the board's bug in its amber for Fixing, a checkbox
                      in the in-review violet for Upkeep. The mark comes from
                      meansMark, the one place both this and the card face read, so
                      they can never draw a type two different ways. Not the faint
                      mono ref it was, and not a chip — where the word has room, the
                      word carries the colour (#302, Vinnie's house rule). */}
                  {(() => {
                    const means = meansOf(issue!.kind, issueTypes);
                    const mark = meansMark(means);
                    return (
                      <div className="read-row">
                        <dt>Type</dt>
                        <dd>
                          <span className={`read-type ${means}`}>
                            <Icon name={mark} size={16} />
                            {labelOf(issue!.kind, issueTypes)}
                          </span>
                        </dd>
                      </div>
                    );
                  })()}

                  {/* Investment class (Run vs Change, #435) — the effective class
                      (override, else type-derived), with a quiet note when a person
                      set it by hand. Always shown: it answers the portfolio's
                      run-vs-change read for this one card. */}
                  {(() => {
                    const eff = investmentClassOf(issue!.kind, issueTypes, issue!.investmentClass, invLifeDefault);
                    return (
                      <div className="read-row">
                        <dt>
                          Investment class
                          <InfoTip label="Investment class">
                            Whether this card is building something new — counted as <b>Change</b>{' '}
                            (investment) — or keeping the service running — counted as <b>Run</b>{' '}
                            (business-as-usual). Set from the card’s type; the portfolio’s
                            run-vs-change split adds it up. Edit the card to override it for one
                            card, or leave it to follow the type.
                            <br />
                            <br />
                            Example: a “patch file servers” card is Run; a “new VPN gateway” is Change.
                          </InfoTip>
                        </dt>
                        <dd>
                          <span className={`read-inv ${INVESTMENT_TONE[eff]}`}>
                            <span className={`inv-swatch ${INVESTMENT_TONE[eff]}`} aria-hidden="true" />
                            {INVESTMENT_LABEL[eff]}
                          </span>
                          {issue!.investmentClass != null && <span className="read-inv-note"> · set manually</span>}
                        </dd>
                      </div>
                    );
                  })()}

                  {/* How essential for the release (MoSCoW) — shown only once set,
                      so a card nobody has prioritised carries no line. Distinct
                      from Priority (urgency) in the triage line above. */}
                  {issue!.moscow && (
                    <div className="read-row">
                      <dt>Release necessity</dt>
                      <dd>
                        <span className={`moscow-chip is-${issue!.moscow}`}>{MOSCOW_LABELS[issue!.moscow]}</span>
                      </dd>
                    </div>
                  )}

                  {/* Release outlook — the honest read on whether this card makes the go-live
                      (Layer B · piece A, releaseChance above). It is one of three shapes, never a
                      number dressed over a contradiction: a real chance for a card that is moving
                      (or not started but on time); an AT-RISK read with NO percentage for a card
                      that is already behind and not moving (a 90% beside an Overdue badge is the
                      lie this removes); and, for a blocked or on-hold card, a muted line saying
                      there is no outlook until that clears — its own banner is the true thing.
                      Clusters with Release necessity: how essential the card is, how likely to
                      make it. Only a Project Manager and above sees it, unless an admin opened it
                      to everyone (maySeeOdds, 0220). */}
                  {releaseChance && (
                    <div className="read-row">
                      <dt>
                        Release Outlook
                        <InfoTip label="Release Outlook">
                          This card’s chance of being finished by the go-live, read from how the
                          work is really going: the board’s own recent pace, how long cards like
                          this (or the owner’s own cards) actually take, and where this card sits
                          in the queue behind the work ahead of it.
                          <br />
                          <br />
                          It never reads more hopeful than the card itself — one that’s blocked, on
                          hold, or already behind and not moving shows that instead of a number. It
                          assumes people are available as usual, so it can’t account for planned
                          leave or a sudden change of pace.
                          <br />
                          <br />
                          Example: “93% to make the release” means that across almost every way the
                          next few weeks could go, this card lands by the go-live.
                        </InfoTip>
                      </dt>
                      <dd>
                        {releaseChance.kind === 'odds' && (
                          <>
                            <span className={`rag ${releaseChance.rag === 'good' ? 'g' : releaseChance.rag === 'warn' ? 'a' : 'r'}`}>
                              <span className="dot" />
                              {Math.round(releaseChance.prob * 100)}% to make the release
                            </span>
                            <span className="read-release-when">
                              {' · go-live '}{humanDay(msToIsoDay(releaseChance.target))}
                              {releaseChance.tentative && ' · not started yet'}
                            </span>
                            {/* THE BASIS (Stage 3) — what the number is read from, in plain words,
                                plus the one thing it can't see, so a manager can trust it or
                                challenge it. The owner's own pace when there's a track record, the
                                board's comparable cards otherwise, the board's throughput alone when
                                neither is deep enough. Never a formula. */}
                            <div className="read-release-caveat">
                              {releaseChance.pace?.source === 'owner'
                                ? `At ${displayName(releaseChance.owner, profiles)}’s own pace over ${releaseChance.pace.sample} similar card${releaseChance.pace.sample === 1 ? '' : 's'}, and where it sits in the queue.`
                                : releaseChance.pace?.source === 'board'
                                  ? `At the pace ${releaseChance.pace.sample} similar card${releaseChance.pace.sample === 1 ? '' : 's'} took, and where it sits in the queue.`
                                  : !issue!.size
                                    ? 'At the board’s recent pace and where this card sits in the queue — with no size set, there’s no read on how long the card itself will take.'
                                    : 'At the board’s recent pace, and where this card sits in the queue.'}
                              {' '}Assumes normal availability — it can’t see leave or time off.
                            </div>
                            {/* The owner's other open load on THIS board — shown as context, not
                                folded into the number (their own pace already prices in normal
                                juggling; the full cross-board load is a later widen). Only when it
                                is enough to be worth saying. Named "on this board" so it is never
                                mistaken for a cross-board total. */}
                            {releaseChance.ownerLoad >= 5 && releaseChance.owner && (
                              <div className="read-release-caveat">
                                {displayName(releaseChance.owner, profiles)} is also carrying{' '}
                                {releaseChance.ownerLoad} other open card{releaseChance.ownerLoad === 1 ? '' : 's'} on this board
                              </div>
                            )}
                          </>
                        )}
                        {releaseChance.kind === 'at-risk' && (
                          <>
                            <span className="rag r">
                              <span className="dot" />
                              At risk
                            </span>
                            <span className="read-release-when">
                              {' · '}
                              {releaseChance.reason === 'overdue-unstarted'
                                ? 'overdue and not started'
                                : releaseChance.reason === 'overdue-stalled'
                                  ? 'overdue and not moving'
                                  : releaseChance.reason === 'stalled'
                                    ? 'no movement in over a week'
                                    : releaseChance.reason === 'over-cycle'
                                      ? 'running longer than cards like this usually take'
                                      : 'behind pace and not started'}
                              {' · go-live '}{humanDay(msToIsoDay(releaseChance.target))}
                            </span>
                            {/* For an aged card, the real numbers behind the verdict — how long it's
                                been in progress against how long cards like it take (Stage 1 fix). */}
                            {releaseChance.reason === 'over-cycle' && releaseChance.age && (
                              <div className="read-release-caveat">
                                {releaseChance.age.inProgressWd} working days in progress — cards like this usually finish in about {releaseChance.age.typicalWd}.
                              </div>
                            )}
                          </>
                        )}
                        {releaseChance.kind === 'paused' && (
                          <span className="read-release-when">
                            {releaseChance.reason === 'on-hold'
                              ? 'On hold — no outlook until it resumes'
                              : 'Blocked — no outlook until it’s cleared'}
                          </span>
                        )}
                      </dd>
                    </div>
                  )}

                  {/* Phase — the same round-dot chip the board used to carry,
                      moved here in the re-cut (#377). */}
                  {(() => {
                    const p = phasesAll.find((x) => x.id === issue!.phase);
                    return p ? (
                      <div className="read-row">
                        <dt>Phase</dt>
                        <dd>
                          <span className="phase-chip">
                            <span className={`phase-chip-dot phase-${p.status}`} />
                            <span className="nm">{p.name}</span>
                            {p.retiredAt ? ' (retired)' : ''}
                          </span>
                        </dd>
                      </div>
                    ) : null;
                  })()}

                  {/* Dates — Target (0106) and Start (0107) each read on their own
                      row (target first), so the start date is no longer a write-only
                      orphan and each can carry its slip: what it is now, what it was
                      first set to, and how far it has drifted (migration 0125, see
                      SlipNote / card-slip.ts). The at-risk glyph sits beside the
                      target when it lands after the card's phase is due to end — the
                      same flag the board face and the edit view carry — with the
                      reason in red below. Overdue (target already past today) keeps
                      its own red note; the two are different warnings. */}
                  {(() => {
                    const s = issue!.startsOn;
                    const d = issue!.dueDate;
                    const startedAt = issue!.startedAt;
                    const completedAt = issue!.completedAt;
                    const hasActual = startedAt != null || completedAt != null;
                    if (!s && !d && !hasActual) return null;
                    const p = phasesAll.find((x) => x.id === issue!.phase);
                    const pastPhase = !!(d && p?.targetEnd && d > p.targetEnd);
                    const overdue = !!(d && cardDueState(d, issue!.status) === 'overdue');
                    const dSlip = dueSlip(issue!);
                    const sSlip = startSlip(issue!);
                    // The reason the actual finish beats the plan, in words, so the
                    // meeting reads the slip without doing the subtraction.
                    const notes: string[] = [];
                    if (s && startedAt != null) { const w = slipWord(msToIsoDay(startedAt), s); if (w) notes.push(`Started ${w}`); }
                    if (d && completedAt != null) { const w = slipWord(msToIsoDay(completedAt), d); if (w) notes.push(`finished ${w}`); }
                    const finishedLate = !!(d && completedAt != null && slipWord(msToIsoDay(completedAt), d)?.endsWith('late'));
                    return (
                      <>
                        {d && (
                          <div className="read-row">
                            <dt>Due date</dt>
                            <dd>
                              <span className="read-dates">
                                <span className={overdue ? 'read-over read-date-cur' : 'read-date-cur'}>{humanDay(d)}</span>
                                {dSlip && <SlipNote slip={dSlip} over={overdue} />}
                                {pastPhase && (
                                  <span
                                    className="read-atrisk"
                                    title={`After the ${p?.name} phase ends (${p?.targetEnd ? shortDay(p.targetEnd) : ''}) — this card is at risk.`}
                                  >
                                    <Icon name="warning" size={14} />
                                  </span>
                                )}
                              </span>
                              {overdue && (
                                <span className="read-date-warn">
                                  Overdue by {daysPast(d)} day{daysPast(d) === 1 ? '' : 's'}
                                </span>
                              )}
                              {pastPhase && (
                                <span className="read-date-warn">
                                  Due date lands after the {p?.name} phase ends ({p?.targetEnd ? shortDay(p.targetEnd) : ''}) — this card is at risk.
                                </span>
                              )}
                            </dd>
                          </div>
                        )}
                        {s && (
                          <div className="read-row">
                            <dt>Start date</dt>
                            <dd>
                              <span className="read-dates">
                                <span className="read-date-cur">{humanDay(s)}</span>
                                {sSlip && <SlipNote slip={sSlip} />}
                              </span>
                            </dd>
                          </div>
                        )}
                        {hasActual && (
                          <div className="read-row">
                            <dt>Actual</dt>
                            <dd>
                              <span className="read-dates">
                                {startedAt != null && completedAt != null ? (
                                  <>
                                    {msHumanDay(startedAt)}
                                    <span className="read-dates-arrow" aria-hidden="true">→</span>
                                    <span className={finishedLate ? 'read-over' : undefined}>{msHumanDay(completedAt)}</span>
                                  </>
                                ) : startedAt != null ? (
                                  // Read in step with the lane: a card the board says is
                                  // actively being worked (In Progress / In Review) shows only
                                  // its real start — the status badge already says it is live.
                                  // A card started then moved back to To Do / Backlog is NOT
                                  // running, so it reads "· on hold" instead of pretending to be.
                                  issue!.status === 'inprogress' || issue!.status === 'inreview' ? (
                                    <>Started {msHumanDay(startedAt)}</>
                                  ) : (
                                    <>Started {msHumanDay(startedAt)} · <span className="read-parked">{ACTUAL_PARKED_LABEL}</span></>
                                  )
                                ) : (
                                  <>Finished {msHumanDay(completedAt!)}</>
                                )}
                              </span>
                              {notes.length > 0 && <span className="read-actual-note">{notes.join(' · ')}</span>}
                            </dd>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* HOW FAR ALONG — read-only, for a viewer or a card you cannot
                      edit. The bar + figure + the health chip, no control. Shown
                      whenever a % is recorded, with or without a due date. */}
                  {(() => {
                    // A date-less card falls back to its phase's window (progress-health.ts).
                    const phaseWin = issue!.phase ? phasesAll.find((x) => x.id === issue!.phase) : undefined;
                    // The EFFECTIVE % — typed when set, else derived from the ticked
                    // Definition-of-Done — so a checklist-tracked card still reads here.
                    const roEff = effectiveProgress(issue!, meta.doneChecks);
                    const ph = healthOfIssue({ ...issue!, progressPct: roEff.pct }, undefined, phaseWin, workCalendar);
                    if (ph.kind === 'none') return null;
                    const rag = ph.tone === 'good' ? 'g' : ph.tone === 'warn' ? 'a' : 'r';
                    return (
                      <div className="read-row">
                        <dt>Progress</dt>
                        <dd>
                          <div className="prog-ro">
                            <span className="prog-fig">{ph.pct}%</span>
                            <ProgressMini health={ph} showPct={false} showDot={false} source={roEff.source} checklistCount={{ done: clDone, total: clTotal }} />
                            {ph.kind === 'tracking' ? (
                              <span className="prog-reason" style={{ color: 'var(--t3)' }}>
                                No due date — set one to track against the calendar.
                              </span>
                            ) : ph.kind === 'on-hold' ? (
                              /* The "On Hold" state is shown once, as the red badge in
                                 the header beside Resume — not repeated here (Vinnie,
                                 25 Oct). */
                              null
                            ) : (
                              ph.label && (
                                <span className={`rag ${rag}`}>
                                  <span className="dot" />
                                  {ph.label}
                                </span>
                              )
                            )}
                          </div>
                          {/* OVERRIDDEN STATE — a typed % is standing in for the card's
                              own checklist. Say so, name what the checklist would read,
                              and offer one click back to it (Finding A, the mock's
                              overridden panel). Only when a checklist actually exists —
                              a plain manual % on a list-less card speaks for itself. */}
                          {issue!.progressPct != null && clTotal > 0 && (
                            <span className="prog-hint">
                              <b>Set by hand.</b> Overrides the checklist ({clDone} of {clTotal} done = {clPct}%).
                              {canEditIssue && (
                                <>
                                  {' '}
                                  <button
                                    type="button"
                                    className="link prog-revert"
                                    onClick={() => {
                                      updateIssue(issue!.id, { progressPct: null });
                                      setProg('');
                                    }}
                                  >
                                    Use the checklist instead
                                  </button>
                                </>
                              )}
                            </span>
                          )}
                        </dd>
                      </div>
                    );
                  })()}

                  {issue!.epic && (
                    <div className="read-row">
                      <dt>Epic</dt>
                      <dd>
                        <span className="epic-chip">{issue!.epic}</span>
                      </dd>
                    </div>
                  )}

                  {issue!.size && (
                    <div className="read-row">
                      <dt>Size</dt>
                      <dd>
                        <span className="size-chip">{SIZE_LABEL[issue!.size]}</span>
                        <span className="read-hint">{SIZE_HINT[issue!.size]}</span>
                      </dd>
                    </div>
                  )}

                  {/* Area — same chip as the backlog row, same colour as the
                      board rail. One colour, three shapes, learned once. */}
                  {(() => {
                    const a = areas.find((x) => x.id === issue!.area);
                    return a ? (
                      <div className="read-row">
                        <dt>Area</dt>
                        <dd>
                          <span className="area-chip">
                            <span className="area-dot" style={{ background: a.colour }} />
                            <span className="nm">{a.name}</span>
                            {a.retiredAt ? ' (retired)' : ''}
                          </span>
                        </dd>
                      </div>
                    ) : null;
                  })()}

                  {required.length > 0 && issue!.status !== 'done' && (
                    <div className="read-row">
                      <dt>Checks</dt>
                      <dd>
                        <span className={`check-count ${missing.length === 0 || doneViaChecklist ? 'is-ready' : ''}`}>
                          {required.length - missing.length}/{required.length} checks
                        </span>
                        <span className="read-hint">
                          {missing.length === 0
                            ? 'ready to be done'
                            : doneViaChecklist
                              ? `${missing.length} still to do — the checklist can complete this card`
                              : `${missing.length} still to do before this can be done`}
                        </span>
                      </dd>
                    </div>
                  )}

                  {/* Sprint — named, not just "in a sprint", and the place work
                      gets PUT in one (CardSprint) when you may plan. Shown only
                      when it has something to say, so the label never dangles. */}
                  {sprintRowShows && (
                    <div className="read-row">
                      <dt>Sprint</dt>
                      <dd>
                        {mayPlan ? (
                          <CardSprint issue={issue!} />
                        ) : (
                          <span className="epic-chip sprint-chip">
                            {sprints.find((s) => s.id === issue!.sprint)?.name ?? 'A finished sprint'}
                          </span>
                        )}
                      </dd>
                    </div>
                  )}

                  {/* Labels — a label that merely echoes the card's own type is
                      not drawn (#335); it is still in the edit box below. */}
                  {issue!.labels.filter((l) => !echoesTheType(l, issue!.kind, issueTypes)).length > 0 && (
                    <div className="read-row">
                      <dt>Labels</dt>
                      <dd>
                        {issue!.labels
                          .filter((l) => !echoesTheType(l, issue!.kind, issueTypes))
                          .map((l) => {
                            // A managed label with a colour shows a small dot on
                            // the otherwise-neutral chip — never a tinted fill, so
                            // it can't be mistaken for an epic chip or an area rail.
                            const c = labelColour(l, boardLabels);
                            return (
                              <span key={l} className={`epic-chip label-chip${c ? ' has-colour' : ''}`}>
                                {c && <span className="label-dot" style={{ background: c }} aria-hidden="true" />}
                                {l}
                              </span>
                            );
                          })}
                      </dd>
                    </div>
                  )}

                  {(issue!.builds ?? []).length > 0 && (
                    <div className="read-row">
                      <dt>Shipped in</dt>
                      <dd>
                        {issue!.builds!.map((n) => (
                          <span key={n} className="build-chip small">
                            build {n}
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}

          {/* THE CARD'S OWN CHECKLIST — the FIRST of the free-text sections, read
              BELOW the Details (the options: Epic, Size, Area, Labels…) and ABOVE the
              summary prose (Vinnie, 27 Oct: options above, checklist leads what you
              type). Tick in place, one click, no edit mode (project-card-checklist).
              Only when the card has a list. */}
          {!isNew && !editing && checklist.length > 0 && (
            <CardChecklist
              items={checklist}
              editing={false}
              canEdit={canEditIssue}
              onChange={commitChecklist}
            />
          )}

          {/* THE PLAIN-ENGLISH SUMMARY + MORE DETAIL — both read BEFORE the feed, so
              "what is this?" is answered before the conversation about it, and BOTH
              are collapsible so a glance stays compact (space is at a premium —
              Vinnie, L150, 24 Oct). "What is this about?" opens by default (it is
              short and the primary glance); "More Detail" sits DIRECTLY beneath it
              and starts collapsed (it can run long) — it used to hide below the feed.
              When there is no summary, the prompt to add one takes its place.
              Read-mode only, like the feed below. */}
          {!isNew && !editing &&
            (issue!.summary ? (
              <Reveal summary={WORDS.summaryLabel} open>
                <div className="read-summary">
                  <IssueText text={issue!.summary} />
                </div>
              </Reveal>
            ) : (
              canEditIssue && (
                <button
                  {...inert('read-empty', () => { setEditing(true); setFocusSummaryWanted(true); })}
                >
                  {WORDS.summaryMissing}
                </button>
              )
            ))}
          {!isNew && !editing && issue!.description && (
            <Reveal summary={WORDS.notes}>
              <div className="read-desc">
                <IssueText text={issue!.description} />
              </div>
            </Reveal>
          )}

          {/* THE CARD FEED — Updates & comments as one stream, below the summary and
              the Details it narrates (0138/0139, dev-preview/card-feed). Read-mode
              only, like the facts above: editing is for changing the card's
              fields; this is for narrating where the work stands and the
              conversation around it. It owns its own drafts and tells us when one
              is unsent, so closing can warn rather than bin it (ledger L54). */}
          {!isNew && !editing && (
            <CardFeed issue={issue!} canPost={canComment} role={role} onDraft={setFeedDraft} stagingEnabled={stagingOn} />
          )}

          {editing && source === 'tracker' && (
            <div className="owned-note">
              Most of this comes from <strong>{sourceName}</strong> and is read-only here — the
              next update from there would overwrite anything typed. Comments, assignee and the
              blocker classification belong to the board and are yours to change.
            </div>
          )}

          {editing ? (
            <div className="card-edit">
              {/* THE PROPERTIES — a calmed, two-tiered label→value list in place
                  of the old flat grid (card-detail cleanup, approved mock
                  dev-preview/card-detail-cleanup/). The essentials sit in an
                  always-open group; everything else is demoted to a collapsible
                  Classification group. An UNSET optional field recedes to a quiet
                  "+ add" chip, and Progress is one calm line. Each field's
                  guidance lives in its circled-(i), so a filled card reads as a
                  column of answers, not a wall of controls. */}

              {/* ── STATUS & OWNERSHIP — the essentials. Folds on the one shared
                  affordance, but defaults open always (card-modal-collapse v2). ── */}
              <div className="calm-sec">
                <button
                  type="button"
                  className="calm-sec-h"
                  aria-expanded={statusOpen}
                  onClick={() => setStatusToggled(!statusOpen)}
                >
                  <span className={`chev${statusOpen ? ' is-open' : ''}`} aria-hidden="true">
                    <Icon name="chevron" size={18} />
                  </span>
                  <span>Status &amp; Ownership</span>
                  <span className="cnt">
                    <span className={`status-dot dot-${status}`} aria-hidden="true" />
                    {STATUS_LABELS[status]}
                    {responsible.trim() && ` · ${whoIs(responsible)}`}
                  </span>
                </button>
                <Expander open={statusOpen}>
                <div className="props calm">
                  <div className="prop" data-focus="status">
                    <span className="lbl">Status</span>
                    <span className="val">
                      <PickField
                        ariaLabel="Status"
                        value={status}
                        disabled={!mine('status')}
                        options={statusChoices.map((s) => ({
                          value: s,
                          label: STATUS_LABELS[s],
                          mark: <span className={`status-dot dot-${s}`} aria-hidden="true" />,
                        }))}
                        onChange={(v) => {
                          const next = v as IssueStatus;
                          setStatus(next);
                          // Work is starting from inside the card — ask who's doing
                          // it, once, by opening the Responsible field below.
                          if (next === 'inprogress' && !responsible.trim() && !doerDismissed.current) {
                            setDoerAsk(true);
                            requestAnimationFrame(() =>
                              respRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
                            );
                          }
                        }}
                      />
                    </span>
                  </div>

                  <div className="prop">
                    <span className="lbl">Priority</span>
                    <span className="val">
                      <PickField
                        ariaLabel="Priority"
                        value={priority}
                        disabled={!mine('priority')}
                        options={PRIORITY_ORDER.map((p) => ({
                          value: p,
                          label: PRIORITY_LABELS[p],
                          mark: <PriorityGlyph priority={p} />,
                        }))}
                        onChange={(v) => setPriority(v as IssuePriority)}
                      />
                    </span>
                  </div>

                  {/* WHO DOES THE WORK (Responsible) sits ABOVE WHO ANSWERS FOR IT
                      (Accountable). Same control, same people, same write path —
                      the two slots differ only in which RACI role they name. */}
                  <div className={`prop${doerAsk ? ' prop-asking' : ''}`} ref={respRowRef} data-focus="assignee">
                    <span className="lbl">
                      Responsible
                      <InfoTip label="Responsible">
                        The one person doing the work on this card — this can be a vendor.
                      </InfoTip>
                    </span>
                    <span className="val">
                      <PickField
                        /* Remount so a fresh instance opens (autoOpen is read once at
                           mount) when the Status change above asks for a doer. */
                        key={doerAsk ? 'resp-ask' : 'resp'}
                        ariaLabel="Responsible"
                        value={responsible}
                        autoOpen={doerAsk}
                        options={[
                          { value: '', label: 'Unassigned' },
                          ...assignable.map((m) => ({ value: m, label: displayName(m, profiles), trailing: <VendorTag who={m} profiles={profiles} compact /> })),
                          ...assistantsOf(meta).map((a) => ({
                            value: assistantAssignee(a.id),
                            label: a.name?.trim() || a.id,
                            group: 'Assistants',
                          })),
                          ...(responsible && !assignable.includes(responsible) && !assigneeAssistant(responsible, meta)
                            ? [{ value: responsible, label: responsible, trailing: <VendorTag who={responsible} profiles={profiles} compact /> }]
                            : []),
                        ]}
                        onChange={setResponsible}
                        /* Only while asking — so the normal field keeps its card
                           focus-restore behaviour (PickField ties that to onClose). */
                        onClose={
                          doerAsk
                            ? (reason) => {
                                // Closed without naming anyone — don't re-open for this card.
                                if (reason !== 'pick') doerDismissed.current = true;
                                setDoerAsk(false);
                              }
                            : undefined
                        }
                      />
                    </span>
                  </div>

                  <div className="prop">
                    <span className="lbl">
                      Accountable
                      <InfoTip label="Accountable">
                        The one person who answers for this card and signs it off — often the project manager.
                      </InfoTip>
                    </span>
                    <span className="val">
                      <PickField
                        ariaLabel="Accountable"
                        value={assignee}
                        options={[
                          { value: '', label: 'Unassigned' },
                          ...assignable.map((m) => ({ value: m, label: displayName(m, profiles), trailing: <VendorTag who={m} profiles={profiles} compact /> })),
                          ...assistantsOf(meta).map((a) => ({
                            value: assistantAssignee(a.id),
                            label: a.name?.trim() || a.id,
                            group: 'Assistants',
                          })),
                          ...(assignee && !assignable.includes(assignee) && !assigneeAssistant(assignee, meta)
                            ? [{ value: assignee, label: assignee, trailing: <VendorTag who={assignee} profiles={profiles} compact /> }]
                            : []),
                        ]}
                        onChange={setAssignee}
                      />
                    </span>
                  </div>

                  {/* DATES — Start and Due consolidated into one "12 Oct → 24 Oct"
                      row, both still editable via the forgiving DateField; the
                      due-past-phase ⚠ rides the due value. Unset → "+ Dates". */}
                  <Collapsible show={shows('dates') || showDueAssist} className="cap cap-span">
                    {shows('dates') && <RemoveX f="dates" label="Dates" />}
                    <div className="prop prop-wide" data-focus="dates">
                      <span className="lbl">
                        Dates
                        <InfoTip label="Dates">
                          Set the day work is meant to begin and the day it should be done by — e.g.
                          next Monday to the 20th. The pair gives the card a span, not just a deadline;
                          it drives what's due soon, what's overdue, and the "coming up" list. Leave
                          either blank if there's no date to set.
                        </InfoTip>
                      </span>
                      <span className="val">
                        <span className="dates-span">
                          {/* Forgiving typed fields — the native date box lost a
                              hand-typed day; these read what a person writes and show
                              the day it resolved to. See DateField. */}
                          <DateField ariaLabel="Start date" value={start} onChange={setStart} onInvalidChange={badDates.mark('start')} />
                          <span className="arrow" aria-hidden="true">→</span>
                          <DateField ariaLabel="Due date" value={due} onChange={setDue} onInvalidChange={badDates.mark('due')} />
                          {/* Past its phase window: a compact red caution glyph riding
                              the due value, the reason on hover. */}
                          {duePastPhase && (
                            <span className="due-flag">
                              <button
                                type="button"
                                className="due-flag-btn"
                                aria-label={`At risk — after the ${duePhase?.name} phase ends (${duePhase?.targetEnd ? shortDay(duePhase.targetEnd) : ''})`}
                              >
                                <Icon name="warning" size={15} />
                              </button>
                              <span className="due-flag-pop" role="note">
                                After the {duePhase?.name} phase ends ({duePhase?.targetEnd ? shortDay(duePhase.targetEnd) : ''}) — this card is at risk.
                              </span>
                            </span>
                          )}
                        </span>
                      </span>
                    </div>
                  </Collapsible>
                  {/* The due offer rides its own full-width row below the Dates field. */}
                  {showDueAssist && dueSuggestion && (
                      <p className="assist-nudge">
                        <span className="assist-ic" aria-hidden="true"><Icon name="sparkle" size={12} /></span>
                        <span className="assist-body">
                          Due <strong>{shortDay(dueSuggestion.due)}</strong>
                          {dueSuggestion.low && dueSuggestion.high && (
                            <span className="assist-range"> ({shortDay(dueSuggestion.low)}–{shortDay(dueSuggestion.high)})</span>
                          )}? — {dueSuggestion.basis}.{' '}
                          <button
                            type="button"
                            className="assist-use"
                            onClick={() => setDue(dueSuggestion.due)}
                          >
                            Use this date
                          </button>
                        </span>
                        <button
                          type="button"
                          className="assist-x"
                          aria-label="Not now"
                          title="Not now"
                          onClick={() => dismissAssist(dueAssistKey)}
                        >
                          ×
                        </button>
                      </p>
                  )}

                  {/* HOW FAR ALONG — one calm line: the read-only ProgressMini bar
                      (with the pace tick) + a right-sized % box + the health tone
                      chip. The "Calendar expects…" caption and the full reasoning
                      sentence move into the control's tooltip and the field's (i).
                      Unset → "+ Progress". */}
                  <Collapsible show={shows('progress')} className="cap cap-span">
                  {!progDerived && <RemoveX f="progress" label="Progress" />}
                  {(() => {
                    const setCls = progH.tone === 'good' ? 'is-good' : progH.tone === 'warn' ? 'is-warn' : progH.tone === 'bad' ? 'is-bad' : '';
                    const rag = progH.tone === 'good' ? 'g' : progH.tone === 'warn' ? 'a' : 'r';
                    return (
                      <div className="prop prop-wide">
                        <span className="lbl">
                          Progress
                          <InfoTip label="Progress">
                            Set how far along the work is, from 0 to 100%. Read against the start and due
                            dates it shows whether the pace will make the date. Leave it blank if you are
                            not tracking a percentage on this card.
                            {progReason && (
                              <>
                                <br />
                                <br />
                                {progReason}
                              </>
                            )}
                          </InfoTip>
                        </span>
                        <span className="val">
                          <div
                            className={`prog-set compact ${setCls}`}
                            style={{ '--val': `${progN ?? 0}%`, '--exp': `${progH.expectedPct ?? 0}%` } as React.CSSProperties}
                          >
                            <ProgressMini health={progH} showPct={false} showDot={false} source={progEff.source} checklistCount={{ done: clDone, total: clTotal }} />
                            <span className="prog-num-wrap compact">
                              <input
                                className="input prog-num"
                                type="number"
                                min={0}
                                max={100}
                                value={prog}
                                placeholder="—"
                                disabled={!mine('progressPct')}
                                aria-label="Progress percent"
                                onChange={(e) => setProg(e.target.value)}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                              <span className="prog-num-pct">%</span>
                            </span>
                            {progH.pct != null && progH.kind !== 'none' && progH.kind !== 'tracking' && progH.label && (
                              <span className={`rag ${rag}`}>
                                <span className="dot" />
                                {progH.label}
                              </span>
                            )}
                          </div>
                          {/* Leaving the box blank keeps the card counting from the
                              list that is driving it — the card's own checklist, or
                              the ticked Definition of Done — say so, with the figure.
                              When a typed % OVERRIDES the card's own checklist, flip to
                              the overridden helper: name what the checklist would read
                              and offer one click back to it (Finding A, the mock's
                              overridden panel). A typed % on a list-less card speaks for
                              itself, so neither helper shows there. */}
                          {/* The helper rides an Expander so it EASES in rather than
                              shoving the sections below it the instant a card's first
                              checklist item makes the % derived (Vinnie, 28 Oct). */}
                          <Expander open={progDerived || (progN != null && clTotal > 0)}>
                            {progDerived ? (
                              <span className="prog-hint">
                                {progEff.source === 'card-checklist'
                                  ? `Leave blank to count from the checklist below (now ${progEff.pct}%)`
                                  : `Leave blank to count from the done checks below (now ${progEff.pct}%)`}
                              </span>
                            ) : progN != null && clTotal > 0 ? (
                              <span className="prog-hint">
                                <b>Set by hand.</b> Overrides the checklist ({clDone} of {clTotal} done = {clPct}%).
                                {mine('progressPct') && (
                                  <>
                                    {' '}
                                    <button
                                      type="button"
                                      className="link prog-revert"
                                      onClick={() => setProg('')}
                                    >
                                      Use the checklist instead
                                    </button>
                                  </>
                                )}
                              </span>
                            ) : null}
                          </Expander>
                        </span>
                      </div>
                    );
                  })()}
                  </Collapsible>

                  {(!shows('dates') || !shows('progress') || !shows('checklist')) && (
                    <div className="prop-add">
                      {!shows('dates') && (
                        <button type="button" className="add-chip" onClick={() => reveal('dates')}>
                          <span className="plus">+</span> Dates
                        </button>
                      )}
                      {!shows('progress') && (
                        <button type="button" className="add-chip" onClick={() => reveal('progress')}>
                          <span className="plus">+</span> Progress
                        </button>
                      )}
                      {!shows('checklist') && (
                        <button type="button" className="add-chip" onClick={() => reveal('checklist')}>
                          <span className="plus">+</span> Checklist
                        </button>
                      )}
                    </div>
                  )}

                </div>
                </Expander>
              </div>

              {/* ── CLASSIFICATION — demoted + collapsible ─────────────────── */}
              <div className="calm-sec">
                <button
                  type="button"
                  className="calm-sec-h"
                  aria-expanded={classOpen}
                  onClick={() => setClassToggled(!classOpen)}
                >
                  <span className={`chev${classOpen ? ' is-open' : ''}`} aria-hidden="true">
                    <Icon name="chevron" size={18} />
                  </span>
                  <span>Classification</span>
                  <span className="cnt">{classHasSet ? `${classSetN + 1} set` : 'none set — add'}</span>
                </button>
                <Expander open={classOpen}>
                  <div className="props calm">
                    {/* Type — always has a value, so always a row. */}
                    <div className="prop">
                      <span className="lbl">Type</span>
                      <span className="val">
                        <PickField
                          ariaLabel="Type"
                          value={kind}
                          disabled={!mine('kind')}
                          options={[
                            ...liveTypes(issueTypes).map((t) => ({ value: t.slug, label: t.name })),
                            ...(kind && !liveTypes(issueTypes).some((t) => t.slug === kind)
                              ? [{ value: kind, label: `${labelOf(kind, issueTypes)} (retired)` }]
                              : []),
                          ]}
                          onChange={(v) => setKind(v as IssueKind)}
                        />
                      </span>
                    </div>

                    {/* RUN vs CHANGE — the card's investment class (#435). Always
                        derives from the type, so always a row, on ONE line: the
                        value with a muted "· from type / · set here" qualifier. The
                        full provenance and "Reset to auto" live in the (i). */}
                    <div className="prop">
                      <span className="lbl">
                        Investment class
                        <InfoTip label="Investment class">
                          Whether this card is building something — a feature or improvement, counted as
                          Change (investment) — or keeping the service running — an ad-hoc fix or upkeep,
                          counted as Run (business-as-usual). It is set from the card’s type, and the
                          portfolio’s run-vs-change split adds it up. Override it here when a single card
                          is funded differently; “Reset to auto” follows the type again.
                          <br />
                          <br />
                          Example: a “patch file servers” card is Run; a “new VPN gateway” is Change.
                          <br />
                          <br />
                          {invClass === null ? (
                            <>Set from the type — {labelOf(kind, issueTypes)} ({meansOf(kind, issueTypes)}).</>
                          ) : (
                            <>
                              Set manually on this card.{' '}
                              {mine('kind') && (
                                <button type="button" className="link" onClick={() => setInvClass(null)}>
                                  Reset to auto
                                </button>
                              )}
                            </>
                          )}
                        </InfoTip>
                      </span>
                      <span className="val">
                        <InvestmentField
                          value={investmentClassOf(kind, issueTypes, invClass, invLifeDefault)}
                          derived={derivedInvestmentClass(kind, issueTypes, invLifeDefault)}
                          overridden={invClass !== null}
                          disabled={!mine('kind')}
                          provenance={invClass === null ? '· from type' : '· set here'}
                          onChange={(next) => {
                            if (next === 'auto') setInvClass(null);
                            else setInvClass(next === derivedInvestmentClass(kind, issueTypes, invLifeDefault) ? null : next);
                          }}
                        />
                      </span>
                    </div>

                    <Collapsible show={shows('area')} className="cap">
                      <RemoveX f="area" label="Area" />
                      <div className="prop">
                        <span className="lbl">
                          Area
                          <InfoTip label="Area">
                            The part of the product this card touches — e.g. Network, Security, Desktop. An
                            area is a standing part of the estate, so it never finishes; for a push of work
                            with an end date, use Epic instead. Type to pick one or create a new one.
                          </InfoTip>
                        </span>
                        <span className="val">
                          <AreaField
                            value={area}
                            areas={offerable}
                            disabled={!mine('area')}
                            onChange={setArea}
                            onCreate={(name) => createArea({ name, colour: nextAreaColour(areas) })}
                          />
                          {areaMisfiled && mine('area') && (
                            <p className="area-nudge">
                              <span>
                                “{areaMisfiled.valueLabel}” looks like a {areaMisfiled.fieldLabel}, not an area —{' '}
                                {areaMisfiled.field === 'date'
                                  ? 'use the card’s Start or Due date instead.'
                                  : `the card already has a ${areaMisfiled.fieldLabel} field.`}{' '}
                                {areaMisfiled.field !== 'date' && areaMisfiled.apply && (
                                  <button
                                    type="button"
                                    className="link"
                                    onClick={() => {
                                      applyMisfiled(areaMisfiled);
                                      setArea('');
                                    }}
                                  >
                                    Set the {areaMisfiled.fieldLabel} to “{areaMisfiled.valueLabel}” instead
                                  </button>
                                )}
                              </span>
                            </p>
                          )}
                        </span>
                      </div>
                    </Collapsible>

                    <Collapsible show={shows('epic')} className="cap">
                      <RemoveX f="epic" label="Epic" />
                      <div className="prop">
                        <span className="lbl">
                          Epic
                          <InfoTip label="Epic">
                            A push of related work with a finish line — e.g. "Battery &amp; heat" or "Q3
                            migration". When it is done, it is done. For a standing part of the product that
                            never finishes — Network, Security — use Area instead. Type a new one or pick an
                            existing.
                          </InfoTip>
                        </span>
                        <span className="val">
                          <EpicField
                            value={epic}
                            options={epicOptions}
                            disabled={!mine('epic')}
                            onChange={setEpic}
                          />
                          {/* BOTH nudges can apply at once, and neither hides the other (Vinnie,
                              21 Oct). A value like "High" IS a Priority AND is a substring of an
                              area named "High availability", so area-match.near() flags the area
                              too. The field nudge is the stronger signal, so it leads; the area
                              suggestion follows as the quieter alternative. The person chooses —
                              nothing is suppressed. */}
                          {epicMisfiled && mine('epic') && (
                            <p className="area-nudge">
                              <span>
                                “{epicMisfiled.valueLabel}” looks like a {epicMisfiled.fieldLabel}, not an epic —{' '}
                                {epicMisfiled.field === 'date'
                                  ? 'use the card’s Start or Due date instead.'
                                  : `the card already has a ${epicMisfiled.fieldLabel} field.`}{' '}
                                {epicMisfiled.field !== 'date' && epicMisfiled.apply && (
                                  <button
                                    type="button"
                                    className="link"
                                    onClick={() => {
                                      applyMisfiled(epicMisfiled);
                                      setEpic('');
                                    }}
                                  >
                                    Set the {epicMisfiled.fieldLabel} to “{epicMisfiled.valueLabel}” instead
                                  </button>
                                )}
                              </span>
                            </p>
                          )}
                          {epicLooksLikeArea && area !== epicLooksLikeArea.id && mine('epic') && mine('area') && (
                            <p className="area-nudge">
                              <span className="epic-sw" style={{ background: epicLooksLikeArea.colour || 'var(--t3)' }} aria-hidden="true" />
                              <span>
                                “{epicLooksLikeArea.name}” is one of your areas — a standing part of the product, not a push of work.{' '}
                                <button
                                  type="button"
                                  className="link"
                                  onClick={() => {
                                    setArea(epicLooksLikeArea.id);
                                    setEpic('');
                                  }}
                                >
                                  File it under the area instead
                                </button>
                              </span>
                            </p>
                          )}
                        </span>
                      </div>
                    </Collapsible>

                    <Collapsible show={shows('phase')} className="cap">
                      <RemoveX f="phase" label="Phase" />
                      <div className="prop">
                        <span className="lbl">
                          Phase
                          <InfoTip label="Phase">
                            Put the card in a project stage — e.g. Build or UAT — so it rolls up under that
                            phase on the Plan. Leave it blank if none fit yet.
                          </InfoTip>
                        </span>
                        <span className="val">
                          <PickField
                            ariaLabel="Phase"
                            value={phase}
                            disabled={!mine('phase')}
                            options={[
                              { value: '', label: 'No phase' },
                              ...offerablePhases.map((p) => ({ value: p.id, label: `${p.name}${p.retiredAt ? ' (retired)' : ''}` })),
                            ]}
                            onChange={setPhase}
                          />
                        </span>
                      </div>
                    </Collapsible>

                    <Collapsible show={shows('size') || showSizeAssist} className="cap">
                      {shows('size') && <RemoveX f="size" label="Size" />}
                      <div className="prop" data-focus="size">
                        <span className="lbl">
                          Size
                          {/* The one hint that must change with the answer — Vinnie,
                              22–25 Aug 2026: Size turns a word into a duration, and what
                              "Quick" or "A few days" means is the thing worth saying. It
                              lives in the (i) now, and still tracks the selection. */}
                          <InfoTip label="Size">
                            {size ? SIZE_HINT[size] : 'Among equally urgent work, the shorter job goes first.'}
                          </InfoTip>
                        </span>
                        <span className="val">
                          <PickField
                            ariaLabel="Size"
                            value={size}
                            options={[
                              { value: '', label: 'Not sized' },
                              ...SIZE_ORDER.map((sz) => ({ value: sz, label: SIZE_LABEL[sz] })),
                            ]}
                            onChange={(v) => setSize(v as IssueSize)}
                          />
                        </span>
                      </div>
                    </Collapsible>
                    {/* The size offer rides its OWN full-width row below the field, so the
                        field keeps its size and the Sprint field keeps its column. */}
                    {showSizeAssist && sizeSuggestion && (
                      <p className="assist-nudge">
                        <span className="assist-ic" aria-hidden="true"><Icon name="sparkle" size={12} /></span>
                        <span className="assist-body">
                          Looks like a <strong>{SIZE_WORD[sizeSuggestion.size]}</strong> — {sizeSuggestion.basis}.{' '}
                          <button
                            type="button"
                            className="assist-use"
                            onClick={() => setSize(sizeSuggestion.size)}
                          >
                            Use {SIZE_WORD[sizeSuggestion.size]}
                          </button>
                        </span>
                        <button
                          type="button"
                          className="assist-x"
                          aria-label="Not now"
                          title="Not now"
                          onClick={() => dismissAssist(sizeAssistKey)}
                        >
                          ×
                        </button>
                      </p>
                    )}

                    {/* HOW ESSENTIAL FOR THE RELEASE (MoSCoW) — distinct from
                        Priority (urgency). Plain words, no acronym. */}
                    <Collapsible show={shows('moscow')} className="cap">
                      <RemoveX f="moscow" label="Release necessity" />
                      <div className="prop">
                        <span className="lbl">Release necessity</span>
                        <span className="val">
                          <PickField
                            ariaLabel="Release necessity"
                            value={moscow}
                            disabled={!mine('priority')}
                            options={[
                              { value: '', label: 'Not set' },
                              ...MOSCOW_ORDER.map((m) => ({
                                value: m,
                                label: MOSCOW_LABELS[m],
                                chipClass: `is-${m}`,
                              })),
                            ]}
                            onChange={(v) => setMoscow(v as MoscowLevel | '')}
                          />
                        </span>
                      </div>
                    </Collapsible>

                    <Collapsible show={shows('difficulty')} className="cap">
                      <RemoveX f="difficulty" label="Difficulty level" />
                      <div className="prop">
                        <span className="lbl">Difficulty level</span>
                        <span className="val">
                          <input
                            className="ctl quiet"
                            type="number"
                            min={0}
                            max={100}
                            value={difficulty}
                            placeholder="Not judged"
                            aria-label="Difficulty level"
                            onChange={(e) => setDifficulty(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        </span>
                      </div>
                    </Collapsible>

                    <Collapsible show={inSprints && shows('sprint')} className="cap">
                      <RemoveX f="sprint" label="Sprint" />
                      <div className="prop" data-focus="sprint">
                        <span className="lbl">Sprint</span>
                        <span className="val">
                          <PickField
                            ariaLabel="Sprint"
                            value={sprintId}
                            options={[
                              { value: '', label: 'Backlog — in no sprint' },
                              ...sprints
                                .filter((s) => s.state !== 'closed')
                                .map((s) => ({ value: s.id, label: `${s.name}${s.state === 'active' ? ' (running)' : ''}` })),
                              ...(sprintId
                                ? sprints
                                    .filter((s) => s.id === sprintId && s.state === 'closed')
                                    .map((s) => ({ value: s.id, label: `${s.name} (finished)` }))
                                : []),
                            ]}
                            onChange={setSprintId}
                          />
                        </span>
                      </div>
                    </Collapsible>

                    <Collapsible show={shows('labels')} className="cap cap-span">
                      <RemoveX f="labels" label="Labels" />
                      <div className="prop prop-wide">
                        <span className="lbl">
                          Labels
                          <InfoTip label="Labels">
                            A cross-cutting tag you put on any card — e.g. “security review”, “needs
                            vendor” — then filter the board by it. Type to pick one or create a new one;
                            give a label a colour in Settings → Workflow → Labels.
                          </InfoTip>
                        </span>
                        <span className="val">
                          <LabelField
                            value={labels}
                            labels={boardLabels}
                            disabled={!mine('labels')}
                            onChange={setLabels}
                            onCreate={(name) => createLabel({ name })}
                          />
                        </span>
                      </div>
                    </Collapsible>

                    {/* The unset optional fields recede to a single row of
                        "+ Label" add-chips — click one to promote it to its normal
                        editable row (a local reveal; nothing is written until a
                        value is set). */}
                    {(!shows('area') || !shows('epic') || !shows('phase') || !shows('size') || !shows('moscow') || !shows('difficulty') || (inSprints && !shows('sprint')) || !shows('labels')) && (
                      <div className="prop-add">
                        {!shows('area') && (
                          <button type="button" className="add-chip" onClick={() => reveal('area')}><span className="plus">+</span> Area</button>
                        )}
                        {!shows('epic') && (
                          <button type="button" className="add-chip" onClick={() => reveal('epic')}><span className="plus">+</span> Epic</button>
                        )}
                        {!shows('phase') && (
                          <button type="button" className="add-chip" onClick={() => reveal('phase')}><span className="plus">+</span> Phase</button>
                        )}
                        {!shows('size') && (
                          <button type="button" className="add-chip" onClick={() => reveal('size')}><span className="plus">+</span> Size</button>
                        )}
                        {!shows('moscow') && (
                          <button type="button" className="add-chip" onClick={() => reveal('moscow')}><span className="plus">+</span> Release necessity</button>
                        )}
                        {!shows('difficulty') && (
                          <button type="button" className="add-chip" onClick={() => reveal('difficulty')}><span className="plus">+</span> Difficulty</button>
                        )}
                        {inSprints && !shows('sprint') && (
                          <button type="button" className="add-chip" onClick={() => reveal('sprint')}><span className="plus">+</span> Sprint</button>
                        )}
                        {!shows('labels') && (
                          <button type="button" className="add-chip" onClick={() => reveal('labels')}><span className="plus">+</span> Labels</button>
                        )}
                      </div>
                    )}
                  </div>
                </Expander>
              </div>

              {/* ACTUALLY STARTED / FINISHED — the dates the system stamped when
                  work really began and ended. Read-only facts, never an input:
                  the record of what happened, not a plan to edit. Demoted below
                  the groups, shown only once they exist (Vinnie, 19 Sep). */}
              {(issue?.startedAt != null || issue?.completedAt != null) && (
                <div className="calm-sec">
                  <div className="props calm">
                    {issue?.startedAt != null && (
                      <div className="prop">
                        <span className="lbl">Actually started</span>
                        <span className="val">
                          <span className="fact-ro">{msShortDay(issue.startedAt)}<span className="fact-tag">Recorded</span></span>
                        </span>
                      </div>
                    )}
                    {issue?.completedAt != null && (
                      <div className="prop">
                        <span className="lbl">Actually finished</span>
                        <span className="val">
                          <span className="fact-ro">{msShortDay(issue.completedAt)}<span className="fact-tag">Recorded</span></span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── BEFORE THIS CAN BE DONE (the board Definition of Done) — folds
                  on the one affordance; opens while a gate is unmet, folds at "all
                  met" (card-modal-collapse v2). A phrase heading, so it stays
                  sentence case. */}
              {required.length > 0 && (
                <div className="calm-sec">
                  <button
                    type="button"
                    className="calm-sec-h"
                    aria-expanded={dodOpen}
                    onClick={() => setDodToggled(!dodOpen)}
                  >
                    <span className={`chev${dodOpen ? ' is-open' : ''}`} aria-hidden="true">
                      <Icon name="chevron" size={18} />
                    </span>
                    <span>Before this can be done</span>
                    <span className="cnt">
                      {missing.length > 0 ? `${missing.length} to go` : 'all met'}
                    </span>
                  </button>
                  <Expander open={dodOpen}>
                    <div className="stack">
                      <div className="checks">
                        {required.map((c) => (
                          <label key={c} className={`check ${checks[c] ? 'is-done' : ''}`}>
                            <input
                              type="checkbox"
                              checked={checks[c] === true}
                              onChange={(e) => {
                                setChecks({ ...checks, [c]: e.target.checked });
                                setShowMissing(false);
                              }}
                            />
                            <span>{c}</span>
                          </label>
                        ))}
                      </div>
                      {showMissing && (
                        <div className="login-error">
                          Not ready to be done — still to do: {missing.join(', ')}.
                        </div>
                      )}
                    </div>
                  </Expander>
                </div>
              )}

              {/* The lone <hr> between the DoD and the blockers is gone — the uniform
                  section rhythm carries the separation (card-modal-collapse v2). */}

              {/* DEPENDS ON / BLOCKS — card-to-card dependencies, split out from
                  "What's blocking this" so a link reads as sequencing with its MS-
                  Project type + lag, not a red blocker (Vinnie, 3 Nov). Same data —
                  the card-kind blocker entries — the other kinds stay below. */}
              <DependencySection
                ref={depRef}
                entries={cardDepsOf(blockerEntries)}
                onChange={(nextDeps) => setBlockerEntries([
                  ...otherBlockersOf(blockerEntries),
                  ...nextDeps,
                ])}
                allIssues={allIssues}
                self={{ ref: issue?.ref ?? '', status, startsOn: start || null, dueDate: due || null }}
                me={me}
                enforced={meta.depHardBlock === true}
                workCalendar={workCalendar}
                onMoveStart={(iso) => setStart(iso)}
                readOnly={!mine('blocked')}
              />

              {/* WHAT'S BLOCKING THIS — the OTHER blockers (a person, a decision, a
                  vendor, a test…). Card links moved to Depends on above, so this
                  shows only the non-card entries. */}
              <BlockerSection
                ref={blockerRef}
                entries={otherBlockersOf(blockerEntries)}
                onChange={(nextOthers) => setBlockerEntries([
                  ...nextOthers,
                  ...cardDepsOf(blockerEntries),
                ])}
                allIssues={allIssues}
                assignable={assignable}
                profiles={profiles}
                me={me}
                notifiedEmails={notifiedEmails}
                selfId={issue?.id}
                readOnly={!mine('blocked')}
              />

              {/* ── CHECKLIST — the FIRST of the free-text sections: it sits below
                  all the option/detail fields (Status & Ownership, Classification)
                  and leads the prose you type (summary, More Detail). Vinnie, 27 Oct:
                  the options stay above, the checklist is the first thing you fill in
                  by hand. Its own foldable section; the header carries the title + the
                  "N of M done" count, so CardChecklist drops its own head (hideHeader).
                  Distinct from the board Definition of Done above; its ticks drive the
                  % ahead of the DoD. Shown when the card has a list or the +Checklist
                  chip revealed one; default open. */}
              <Collapsible show={shows('checklist') && canEditIssue}>
                <div className="calm-sec">
                  <button
                    type="button"
                    className="calm-sec-h"
                    aria-expanded={checklistOpen}
                    onClick={() => setChecklistToggled(!checklistOpen)}
                  >
                    <span className={`chev${checklistOpen ? ' is-open' : ''}`} aria-hidden="true">
                      <Icon name="chevron" size={18} />
                    </span>
                    <span>Checklist</span>
                    {clTotal > 0 && (
                      <span className="cnt">{clDone} of {clTotal} done</span>
                    )}
                  </button>
                  <Expander open={checklistOpen}>
                    <CardChecklist
                      items={checklist}
                      editing
                      canEdit={canEditIssue}
                      onChange={commitChecklist}
                      autoFocusComposer={checklist.length === 0}
                      hideHeader
                    />
                  </Expander>
                </div>
              </Collapsible>

              {/* ── WHAT IS THIS ABOUT? (the plain-English summary) — folds on the
                  one affordance; opens by default (the primary glance). A phrase
                  heading, so it stays sentence case. The folded summary is the first
                  line, or the prompt to add one (card-modal-collapse v2). */}
              <div className="calm-sec">
                <button
                  type="button"
                  className="calm-sec-h"
                  aria-expanded={summaryOpen}
                  onClick={() => setSummaryToggled(!summaryOpen)}
                >
                  <span className={`chev${summaryOpen ? ' is-open' : ''}`} aria-hidden="true">
                    <Icon name="chevron" size={18} />
                  </span>
                  <span>{WORDS.summaryLabel}</span>
                  {/* A clean state word, not the summary's first line of prose — a
                      sentence sitting in the header read as clutter and duplicated
                      the editor when open (Vinnie, 27 Oct). Matches More Detail. */}
                  <span className="cnt">{summary.trim() ? 'Written' : 'Add a summary'}</span>
                </button>
                <Expander open={summaryOpen}>
                  <div className="write-field">
                    <span className="lbl"><span className="sub">{WORDS.summaryHint}</span></span>
                    <ListEditable
                      className="ctl summary-box"
                      ariaLabel={WORDS.summaryLabel}
                      disabled={!mine('summary')}
                      value={summary}
                      onChange={setSummary}
                      handleRef={summaryEditor}
                    />
                    {/* #228 — said where the writing happens, not in documentation
                        nobody reads: this line is what the item says in a change
                        request someone signs. Only on boards that work in sprints,
                        which are the ones that produce those documents. */}
                    {inSprints && (
                      <span className="hint">
                        This is what the item says in a change request someone signs — write it for the reader who signs.
                      </span>
                    )}
                  </div>
                </Expander>
              </div>

              {/* ── MORE DETAIL (the fuller description) — folds; defaults FOLDED, it
                  runs long (matches the read-mode L150 decision). The folded summary
                  says whether anything is written (card-modal-collapse v2). */}
              <div className="calm-sec">
                <button
                  type="button"
                  className="calm-sec-h"
                  aria-expanded={detailOpen}
                  onClick={() => setDetailToggled(!detailOpen)}
                >
                  <span className={`chev${detailOpen ? ' is-open' : ''}`} aria-hidden="true">
                    <Icon name="chevron" size={18} />
                  </span>
                  <span>{WORDS.notes}</span>
                  <span className="cnt">{description.trim() ? 'Written' : 'Add detail'}</span>
                </button>
                <Expander open={detailOpen}>
                  <div className="write-field">
                {/* "Where it stands now" used to be a second textarea here, its
                    own field on the card. It is gone (Vinnie, 4 Oct): the
                    Updates & comments feed is now the single place the standing
                    note lives — a status update tells people where the work
                    stands, so the card no longer carries a separate box that
                    the next feed entry would silently overwrite. */}
                {/* The house segmented control, the same one the backlog and
                    people screens use — not a card-only variant. Keeps one
                    segmented control across the app. */}
                <div className="seg write-seg" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!preview}
                    className={`seg-btn ${preview ? '' : 'active'}`}
                    onClick={() => setPreview(false)}
                  >
                    Write description
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={preview}
                    className={`seg-btn ${preview ? 'active' : ''}`}
                    onClick={() => setPreview(true)}
                  >
                    Preview markdown
                  </button>
                </div>
                {preview ? (
                  <div className="write-preview write-body">
                    {description.trim() ? (
                      <IssueText text={description} />
                    ) : (
                      <span className="hint">Nothing written yet.</span>
                    )}
                  </div>
                ) : (
                  <ListEditable
                    className="ctl desc-box"
                    ariaLabel={WORDS.notes}
                    disabled={!mine('description')}
                    value={description}
                    onChange={setDescription}
                  />
                )}
                  </div>
                </Expander>
              </div>
            </div>
          ) : (
            <>
              {/* The plain-English summary now reads ABOVE the feed (see the
                  read-mode block higher up), so "what is this card?" is answered
                  before the conversation. This branch keeps the audit footer and
                  the fuller notes. */}
              {/* Who has the card now rides the triage line at the top, where a
                  PM looks first; this footer keeps only the audit facts — when it
                  was raised and last changed. Start and target dates, and their
                  overdue / at-risk warnings, live in the Dates row of the Details
                  grid above (one home for the card's dates, not two). When nobody
                  holds the card the triage line omits the assignee, so it is said
                  here instead. */}
              <div className="read-meta">
                {!issue!.assignee && <span>Nobody assigned</span>}
                <span>Raised {relativeTime(issue!.createdAt)}</span>
                <span>Last changed {relativeTime(issue!.updatedAt)}</span>
              </div>

              {/* "Where it stands now" used to sit here, repeating the newest
                  feed entry word for word — dropped (Vinnie, 4 Oct): the Updates
                  & comments feed above is now the single place the standing note
                  lives. "More Detail" (the fuller description) used to sit here too,
                  BELOW the feed; it now reads directly under "What is this about?"
                  above, both collapsible (Vinnie, L150, 24 Oct). */}
            </>
          )}

          {/* CODE CHANGES — moved off the smaller <Reveal> onto the one shared
              .calm-sec-h header, so the whole modal has ONE fold style and one
              motion tier (card-modal-collapse v2). Folded by default; the count
              rides the summary. */}
          {!isNew && (issue!.commits ?? []).length > 0 && (
            <div className="calm-sec">
              <button
                type="button"
                className="calm-sec-h"
                aria-expanded={codeOpen}
                onClick={() => setCodeToggled(!codeOpen)}
              >
                <span className={`chev${codeOpen ? ' is-open' : ''}`} aria-hidden="true">
                  <Icon name="chevron" size={18} />
                </span>
                <span>Code changes</span>
                <span className="cnt">{issue!.commits!.length}</span>
              </button>
              <Expander open={codeOpen}>
              <div className="commits">
                {issue!.commits!.map((c) =>
                  repoUrl ? (
                    <a
                      key={c.sha}
                      className="commit"
                      href={`${repoUrl}/commit/${c.sha}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="commit-sha">{c.short}</span>
                      <span className="commit-subject">{c.subject}</span>
                      <span className="commit-date">{c.date}</span>
                    </a>
                  ) : (
                    <div key={c.sha} className="commit">
                      <span className="commit-sha">{c.short}</span>
                      <span className="commit-subject">{c.subject}</span>
                      <span className="commit-date">{c.date}</span>
                    </div>
                  )
                )}
              </div>
              </Expander>
            </div>
          )}

          {/* HISTORY — likewise moved onto the shared .calm-sec-h header; folded by
              default, the count in the summary (card-modal-collapse v2). */}
          {!isNew && (issue!.activity ?? []).length > 0 && (
            <div className="calm-sec">
              <button
                type="button"
                className="calm-sec-h"
                aria-expanded={historyOpen}
                onClick={() => setHistoryToggled(!historyOpen)}
              >
                <span className={`chev${historyOpen ? ' is-open' : ''}`} aria-hidden="true">
                  <Icon name="chevron" size={18} />
                </span>
                <span>History</span>
                <span className="cnt">{issue!.activity!.length}</span>
              </button>
              <Expander open={historyOpen}>
              <div className="activity">
                {[...issue!.activity!]
                  .sort((a, b) => b.at - a.at)
                  .map((a) => (
                    <div key={a.id} className="activity-row">
                      {/* By name, like everywhere else that names somebody.
                          This row printed the raw address while the comments
                          directly below it, and the Activity feed, both used
                          the person's name — the same history read two ways on
                          one screen. `whoIs` also names an assistant properly
                          rather than showing "assistant:claude-cli". */}
                      <span className="activity-who">{whoIs(a.who)}</span>
                      <span className="activity-what">
                        {a.what}
                        {/* What it said before. Printed, because a record kept
                            where nobody can read it is no better than no record
                            — the whole point is that somebody can put the words
                            back by looking. */}
                        {a.was && <span className="activity-was">{a.was}</span>}
                      </span>
                      {/* A clock and a day, not an elapsed count — see
                          historyTime. Reading a history back, "3d ago" is the
                          answer to a question nobody is asking. */}
                      <span className="activity-when">{historyTime(a.at)}</span>
                    </div>
                  ))}
              </div>
              </Expander>
            </div>
          )}

        </div>

        {/* Pinned, so the primary action is never below the fold. */}
        <div className="modal-actions">
          {!isNew && editing && canDelete && (
            <button className="btn btn-danger" onClick={remove}>
              Delete
            </button>
          )}
          {deleteError ? (
            <span className="modal-hint delete-refused" role="alert">
              {deleteError}
            </span>
          ) : (
            editing && (
              <span className="modal-hint">
                Press <b>{modKey()} + Enter</b> to save
              </span>
            )
          )}
          <div className="spacer" />
          {editing ? (
            <>
              {/* Cancel leaves the edit form — back to reading on an existing
                  card, closing a new one (asking first if anything was typed).
                  It no longer shows an "Esc" hint, because Escape now closes the
                  whole card rather than just leaving the form. With no honest key
                  to put on Cancel, neither button carries a key badge — one alone
                  reads as an oversight — and the save shortcut stays discoverable
                  through the "⌘ + Enter to save" hint on the left of this bar. */}
              <button className="btn" onClick={() => (isNew ? askThenDismiss() : leave.current())}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={save} disabled={!title.trim() || badDates.any}>
                {isNew ? 'Create Issue' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button className="btn" onClick={askThenDismiss}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
